import React, { useState, useEffect, useCallback, useRef } from 'react';
import { cheeseAPI, configAPI, proxyImageUrl, productsAPI } from '../api/client';

const _ALL_CHEESE_SIZES = ['35','36','37','38','39','40','41','42','43','44','45','46','47'];

// ── Дроп-зона загрузки TG-фото ────────────────────────────────────────────
function TgPhotoDropZone({ value, onChange }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const fileId = useRef('tgdrop-' + Math.random().toString(36).slice(2));

  const upload = async (file) => {
    if (!file || !file.type.startsWith('image/')) { setErr('Только изображения'); return; }
    setUploading(true); setErr(null);
    try {
      const res = await cheeseAPI.uploadPhoto(file);
      onChange(res.data.url);
    } catch (e) {
      setErr('Ошибка загрузки: ' + (e?.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  return (
    <div>
      <div
        className={`tg-photo-drop${dragging ? ' tg-photo-drop--over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files[0]); }}
        onClick={() => document.getElementById(fileId.current)?.click()}
      >
        <input id={fileId.current} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ''; }} />
        {uploading
          ? <span className="tg-photo-drop-hint">Завантаження…</span>
          : value
            ? <img src={value} alt="TG фото" className="tg-photo-drop-img" />
            : <span className="tg-photo-drop-hint">{dragging ? 'Отпустите фото' : '+ перетащите или нажмите'}</span>
        }
        {value && !uploading && (
          <button className="tg-photo-drop-clear" title="Удалить"
            onClick={e => { e.stopPropagation(); onChange(''); }}>✕</button>
        )}
      </div>
      <input className="mng-input" style={{ marginTop: 4 }} value={value || ''}
        onChange={e => onChange(e.target.value)} placeholder="или вставьте ссылку https://…" />
      {err && <div style={{ color: 'var(--lg-danger, #e55)', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// ── Дроп-зона для файлов комментария (multiple) ────────────────────────────
function CommentFileDropZone({ files, onChange }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (incoming) => {
    const imgs = Array.from(incoming).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    onChange(prev => {
      const names = new Set(prev.map(f => f.name + f.size));
      const unique = imgs.filter(f => !names.has(f.name + f.size));
      return [...prev, ...unique];
    });
  };

  const remove = (idx) => onChange(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="comment-drop-wrap">
      <div
        className={`comment-drop-zone${dragging ? ' comment-drop-zone--over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <span className="comment-drop-hint">
          {dragging ? 'Відпустіть фото' : '+ перетягніть або натисніть для вибору фото'}
        </span>
      </div>
      {files.length > 0 && (
        <div className="comment-drop-previews">
          {files.map((f, i) => (
            <div key={i} className="comment-drop-thumb">
              <img src={URL.createObjectURL(f)} alt={f.name} />
              <button className="comment-drop-thumb-del" onClick={() => remove(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="comment-drop-count">{files.length} фото вибрано</div>
      )}
    </div>
  );
}

// ── Размерный редактор ─────────────────────────────────────────────────────
function SizesEditor({ value, onChange, allSizes = false }) {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const existing = Object.entries(value || {});
    if (existing.length) {
      setEntries(existing.map(([size, qty]) => ({ size, qty: String(qty) })));
    } else if (allSizes) {
      const filled = _ALL_CHEESE_SIZES.map(s => ({ size: s, qty: '0' }));
      setEntries(filled);
      const obj = {};
      filled.forEach(({ size, qty }) => { obj[size] = Number(qty) || 0; });
      onChange(obj);
    } else {
      setEntries([{ size: '', qty: '' }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = (next) => {
    setEntries(next);
    const obj = {};
    next.forEach(({ size, qty }) => {
      if (size.trim()) obj[size.trim()] = Number(qty) || 0;
    });
    onChange(obj);
  };

  const update = (idx, field, val) => sync(entries.map((e, i) => i === idx ? { ...e, [field]: val } : e));
  const add    = () => sync([...entries, { size: '', qty: '' }]);
  const remove = (idx) => sync(entries.filter((_, i) => i !== idx));

  return (
    <div className="cheese-sizes-editor">
      {entries.map((e, idx) => (
        <div key={idx} className="cheese-sizes-row">
          <input
            className="mng-input cheese-size-input"
            placeholder="Размер"
            value={e.size}
            onChange={ev => update(idx, 'size', ev.target.value)}
          />
          <span className="cheese-sizes-x">×</span>
          <input
            className="mng-input cheese-qty-input"
            placeholder="Пар"
            type="text" inputMode="decimal"
            min="0"
            value={e.qty}
            onChange={ev => update(idx, 'qty', ev.target.value)}
          />
          <button className="cheese-sizes-del" type="button" onClick={() => remove(idx)}>✕</button>
        </div>
      ))}
      <button className="cheese-sizes-add" type="button" onClick={add}>+ добавить размер</button>
    </div>
  );
}


function totalPairs(sizes_qty) {
  if (!sizes_qty) return 0;
  return Object.values(sizes_qty).reduce((s, q) => s + (Number(q) || 0), 0);
}

// ── Фото ───────────────────────────────────────────────────────────────────
function CheesePhoto({ url, size = 56 }) {
  const [err, setErr] = useState(false);
  if (!url || err) return (
    <div className="cheese-photo-empty" style={{ width: size, height: size }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="3"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21,15 16,10 5,21"/>
      </svg>
    </div>
  );
  return (
    <img
      src={proxyImageUrl(url)}
      alt=""
      className="cheese-photo-img"
      style={{ width: size, height: size }}
      onError={() => setErr(true)}
    />
  );
}

// ── Секция формы ───────────────────────────────────────────────────────────
function FormSection({ title, children }) {
  return (
    <div className="cheese-form-section">
      {title && <div className="cheese-form-section-title">{title}</div>}
      {children}
    </div>
  );
}

function Field({ label, children, half }) {
  return (
    <div className={`mng-field${half ? ' cheese-field-half' : ''}`}>
      <label className="mng-label">{label}</label>
      {children}
    </div>
  );
}

// ── Модалка ────────────────────────────────────────────────────────────────
function CheeseModal({ title, onClose, onSave, saveLabel = 'Сохранить', loading, disabled, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cheese-modal" onClick={e => e.stopPropagation()}>
        <div className="cheese-modal-header">
          <h3 className="cheese-modal-title">{title}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="cheese-modal-body">
          {children}
        </div>
        <div className="cheese-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" onClick={onSave} disabled={loading || disabled}>
            {loading ? 'Сохранение…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Модалка заказа ─────────────────────────────────────────────────────────
function OrderModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    name: '', photo_url: '', catalog_sku: '', sizes_qty: {}, has_box: false,
    ...(item || {}),
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const res = item?.id
        ? await cheeseAPI.updateOrder(item.id, form)
        : await cheeseAPI.createOrder(form);
      onSave(res.data);
      onClose();
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CheeseModal
      title={item?.id ? 'Редактировать заказ' : 'Новая позиция в закупку'}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      disabled={!form.name.trim()}
    >
      <Field label="Название товара">
        <input className="mng-input" value={form.name}
          onChange={e => set('name', e.target.value)} placeholder="Название…" autoFocus />
      </Field>

      <Field label="Артикул каталога">
        <input className="mng-input" value={form.catalog_sku || ''}
          onChange={e => set('catalog_sku', e.target.value)} placeholder="SKU…" />
      </Field>

      <Field label="Ссылка на фото">
        <input className="mng-input" value={form.photo_url || ''}
          onChange={e => set('photo_url', e.target.value)} placeholder="https://…" />
      </Field>

      <Field label="Размеры и количество пар">
        <SizesEditor value={form.sizes_qty} onChange={v => set('sizes_qty', v)} allSizes={!Object.keys(form.sizes_qty || {}).length} />
      </Field>

      <div className="cheese-checkbox-row">
        <label className="cheese-checkbox-label">
          <input type="checkbox" className="cheese-checkbox"
            checked={!!form.has_box} onChange={e => set('has_box', e.target.checked)} />
          <span className="cheese-checkbox-text">Оригинальная коробка (BOX)</span>
        </label>
      </div>
    </CheeseModal>
  );
}

const MATERIALS_BUILTIN = ['Текстиль', 'Сітка', 'Шкіра', 'Нейлон', 'Замша', 'Піна'];
const LS_MATERIALS_KEY = 'cheese_materials_custom';

function loadCustomMaterials() {
  try { return JSON.parse(localStorage.getItem(LS_MATERIALS_KEY) || '[]'); } catch { return []; }
}
function saveCustomMaterials(list) {
  localStorage.setItem(LS_MATERIALS_KEY, JSON.stringify(list));
}

function MaterialsPicker({ value = '', onChange }) {
  const [custom, setCustom] = useState(loadCustomMaterials);
  const [input, setInput] = useState('');

  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
  const all = [...MATERIALS_BUILTIN, ...custom];

  const emit = (list) => onChange(list.join(', '));

  const toggle = (mat) => {
    const next = selected.includes(mat) ? selected.filter(m => m !== mat) : [...selected, mat];
    emit(next);
  };

  const addCustom = () => {
    const mat = input.trim();
    if (!mat) return;
    let nextSelected = selected.includes(mat) ? selected : [...selected, mat];
    if (!all.includes(mat)) {
      const nextCustom = [...custom, mat];
      setCustom(nextCustom);
      saveCustomMaterials(nextCustom);
    }
    emit(nextSelected);
    setInput('');
  };

  return (
    <div>
      {selected.length > 0 && (
        <div className="cheese-materials-summary">{selected.join(', ')}</div>
      )}
      <div className="cheese-materials-grid">
        {all.map(mat => (
          <label key={mat} className="cheese-material-check">
            <input type="checkbox" checked={selected.includes(mat)} onChange={() => toggle(mat)} />
            <span>{mat}</span>
          </label>
        ))}
      </div>
      <div className="cheese-materials-add">
        <input className="mng-input cheese-materials-add-input"
          placeholder="Інший матеріал…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
        <button className="btn btn-secondary btn-sm" type="button" onClick={addCustom}>+</button>
      </div>
    </div>
  );
}

// ── Модалка товара в пути ──────────────────────────────────────────────────
function TransitModal({ item, onClose, onSave }) {
  const empty = {
    name: '', photo_url: '', tg_photo_url: '', catalog_sku: '', supplier_article: '',
    supplier_name: '',
    code: '',
    sizes_qty: {}, purchase_price_per_pair: '', market_price_avg: '',
    manufacturer: "В'єтнам", materials: '', weight_kg: '', boxes_delivery_uah: '',
    order_date: '', china_ship_date: '', status: 'transit',
    category_id_val: '', category_name: '', item_id: '',
    has_box: false, box_type: 'brand', exchange_rate: '', purchase_uah_total: '',
    received_at: '', notes: '',
  };
  const initItem = item ? { ...item, box_type: item.box_type || (item.has_box ? 'original' : 'brand') } : null;
  const [form, setForm] = useState({ ...empty, ...(initItem || {}) });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [prevData, setPrevData] = useState(null);
  const [prevApplied, setPrevApplied] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyPrev = (data) => {
    setForm(f => ({
      ...f,
      manufacturer:    data.manufacturer    || f.manufacturer,
      category_name:   data.category_name   || f.category_name,
      category_id_val: data.category_id_val != null ? data.category_id_val : f.category_id_val,
      supplier_article: data.supplier_article || f.supplier_article,
      supplier_name:   data.supplier_name   || f.supplier_name,
      item_id:         data.item_id != null  ? data.item_id  : f.item_id,
      materials:       data.materials        || f.materials,
    }));
    setPrevApplied(true);
    setPrevData(null);
  };

  useEffect(() => {
    cheeseAPI.listCategories().then(r => setCategories(r.data)).catch(() => {});
    if (!item?.id) {
      cheeseAPI.getNextItemId().then(r => set('item_id', r.data.next_item_id)).catch(() => {});
    }
    if (!item?.exchange_rate) {
      configAPI.getConfig()
        .then(r => {
          const byKey = Object.fromEntries((r.data || []).map(c => [c.key, c.value]));
          if (byKey.exchange_rate) setForm(f => ({ ...f, exchange_rate: f.exchange_rate || byKey.exchange_rate }));
        })
        .catch(() => {});
    }
    if (!item?.id && item?.catalog_sku) {
      cheeseAPI.getPrevTransit(item.catalog_sku)
        // Автоматично переносимо параметри повторного замовлення — без ручного кліку
        .then(r => { if (r.data) applyPrev(r.data); })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sizesRange = (() => {
    const active = Object.entries(form.sizes_qty || {})
      .filter(([, qty]) => Number(qty) > 0)
      .map(([s]) => Number(s)).filter(n => !isNaN(n)).sort((a, b) => a - b);
    if (!active.length) return '';
    return active.length === 1 ? String(active[0]) : `${active[0]}-${active[active.length - 1]}`;
  })();

  const computedUah = (() => {
    const pairs = totalPairs(form.sizes_qty);
    const price = parseFloat(form.purchase_price_per_pair) || 0;
    const rate  = parseFloat(form.exchange_rate) || 0;
    return (pairs > 0 && price > 0 && rate > 0) ? Math.round(pairs * price * rate) : null;
  })();

  const handleCategoryNameChange = (e) => {
    const name = e.target.value;
    const cat = categories.find(c => c.name === name);
    setForm(f => ({ ...f, category_name: name, category_id_val: cat ? cat.category_id : f.category_id_val }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const data = {
        ...form,
        purchase_price_per_pair: form.purchase_price_per_pair !== '' ? Number(form.purchase_price_per_pair) : null,
        market_price_avg:        form.market_price_avg !== ''        ? Number(form.market_price_avg)        : null,
        weight_kg:               form.weight_kg !== ''               ? Number(form.weight_kg)               : null,
        boxes_delivery_uah:      form.boxes_delivery_uah !== ''      ? Number(form.boxes_delivery_uah)      : null,
        category_id_val:         form.category_id_val               ? Number(form.category_id_val)         : null,
        item_id:                 form.item_id                        ? Number(form.item_id)                 : null,
        exchange_rate:           form.exchange_rate !== ''           ? Number(form.exchange_rate)           : null,
        purchase_uah_total:      form.purchase_uah_total !== ''      ? Number(form.purchase_uah_total)      : (computedUah || null),
      };
      const res = item?.id
        ? await cheeseAPI.updateTransit(item.id, data)
        : await cheeseAPI.createTransit(data);
      onSave(res.data);
      onClose();
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CheeseModal
      title={item?.id ? 'Редактировать товар' : 'Новый товар в пути'}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      disabled={!form.name.trim()}
    >
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      {prevApplied && (
        <div className="transfer-prev-applied" style={{ margin: '-4px 0 12px' }}>Параметры перенесены автоматически из предыдущего заказа</div>
      )}
      <FormSection title="Категория / Идентификаторы">
        <div className="cheese-field-row">
          <Field label="Название категории" half>
            <select className="mng-input" value={form.category_name || ''}
              onChange={handleCategoryNameChange}>
              <option value="">— не выбрана —</option>
              {categories.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Код категории" half>
            <input className="mng-input" type="text" inputMode="decimal" value={form.category_id_val || ''}
              readOnly style={{ opacity: 0.7, cursor: 'default' }} placeholder="—" />
          </Field>
        </div>
        <Field label="Item ID">
          <input className="mng-input" type="text" inputMode="decimal" value={form.item_id || ''}
            onChange={e => set('item_id', e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title="Основное">
        <Field label="Название товара">
          <input className="mng-input" value={form.name}
            onChange={e => set('name', e.target.value)} placeholder="Название…" autoFocus />
        </Field>
        <div className="cheese-field-row">
          <Field label="Артикул каталога" half>
            <input className="mng-input" value={form.catalog_sku || ''}
              onChange={e => set('catalog_sku', e.target.value)} />
          </Field>
          <Field label="Артикул поставщика" half>
            <input className="mng-input" value={form.supplier_article || ''}
              onChange={e => setForm(f => ({ ...f, supplier_article: e.target.value, code: e.target.value }))} />
          </Field>
        </div>
        <Field label="Фото товара (ссылка)">
          <input className="mng-input" value={form.photo_url || ''}
            onChange={e => set('photo_url', e.target.value)} placeholder="https://…" />
          {form.photo_url && (
            <div className="cheese-edit-photo-preview">
              <img className="cheese-edit-photo-img" src={form.photo_url} alt="Фото товара" />
            </div>
          )}
        </Field>
        <Field label="Фото для публикации в TG">
          <TgPhotoDropZone value={form.tg_photo_url || ''} onChange={v => set('tg_photo_url', v)} />
        </Field>
      </FormSection>

      <FormSection title="Країна виробництва / Матеріали">
        <Field label="Країна виробництва">
          <select className="mng-input" value={form.manufacturer || "В'єтнам"}
            onChange={e => set('manufacturer', e.target.value)}>
            <option value="В'єтнам">В'єтнам</option>
            <option value="Китай">Китай</option>
          </select>
        </Field>
        <Field label="Матеріали">
          <MaterialsPicker value={form.materials || ''} onChange={v => set('materials', v)} />
        </Field>
      </FormSection>

      <FormSection title="Размеры и количество">
        <SizesEditor value={form.sizes_qty} onChange={v => set('sizes_qty', v)} />
        {sizesRange && <div className="cheese-sizes-range">Диапазон: <b>{sizesRange}</b></div>}
        <Field label="Коробка / упаковка">
          <select className="mng-input" value={form.box_type || 'brand'}
            onChange={e => { const bt = e.target.value; setForm(f => ({ ...f, box_type: bt, has_box: bt === 'original' })); }}>
            <option value="original">Оригінальна коробка 📦</option>
            <option value="brand">Брендова коробка</option>
            <option value="white">Біла коробка</option>
          </select>
        </Field>
      </FormSection>

      <FormSection title="Финансы">
        <div className="cheese-field-row">
          <Field label="Опт / пара, $" half>
            <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.01"
              value={form.purchase_price_per_pair || ''} onChange={e => set('purchase_price_per_pair', e.target.value.replace(',', '.'))} placeholder="0" />
          </Field>
          <Field label="Курс $ → ₴" half>
            <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.5"
              value={form.exchange_rate || ''} onChange={e => set('exchange_rate', e.target.value.replace(',', '.'))} placeholder="46" />
          </Field>
        </div>
        <div className="cheese-field-row">
          <Field label="Закуп ₴ (итого)" half>
            <input className="mng-input" type="text" inputMode="decimal" min="0" step="1"
              value={computedUah ?? form.purchase_uah_total ?? ''}
              readOnly={!!computedUah}
              style={computedUah ? { opacity: 0.75, cursor: 'default', background: 'rgba(74,144,226,0.06)' } : {}}
              onChange={e => set('purchase_uah_total', e.target.value.replace(',', '.'))}
              placeholder="0" />
          </Field>
          <Field label="Ср. рыночная цена, ₴" half>
            <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.01"
              value={form.market_price_avg || ''} onChange={e => set('market_price_avg', e.target.value.replace(',', '.'))} placeholder="0" />
          </Field>
        </div>
        <div className="cheese-field-row">
          <Field label="Вес партии, кг" half>
            <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.1"
              value={form.weight_kg || ''} onChange={e => set('weight_kg', e.target.value.replace(',', '.'))} placeholder="0" />
          </Field>
          <Field label="Коробки + доставка, ₴" half>
            <input className="mng-input" type="text" inputMode="decimal" min="0" step="1"
              value={form.boxes_delivery_uah || ''} onChange={e => set('boxes_delivery_uah', e.target.value.replace(',', '.'))} placeholder="0" />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Даты">
        <div className="cheese-field-row">
          <Field label="Дата заказа" half>
            <input className="mng-input" type="date" value={form.order_date || ''}
              onChange={e => set('order_date', e.target.value)} />
          </Field>
          <Field label="Отправка из Китая" half>
            <input className="mng-input" type="date" value={form.china_ship_date || ''}
              onChange={e => set('china_ship_date', e.target.value)} />
          </Field>
        </div>
        {form.status === 'received' || form.received_at ? (
          <Field label="Дата получения">
            <input className="mng-input" type="date" value={form.received_at || ''}
              onChange={e => set('received_at', e.target.value)} />
          </Field>
        ) : null}
      </FormSection>
      <FormSection title="Заметки">
        <textarea
          className="mng-input"
          rows={4}
          style={{ resize: 'vertical', minHeight: 72 }}
          placeholder="Любые заметки по товару…"
          value={form.notes || ''}
          onChange={e => set('notes', e.target.value)}
        />
      </FormSection>

      </div>{/* end left column */}
      <div className="rcv-sup-panel">
        <div className="rcv-sup-title">Наличие у поставщиков</div>
        <SupplierPanel catalogSku={form.catalog_sku} />
      </div>
      </div>{/* end flex wrapper */}
    </CheeseModal>
  );
}

// ── Модалка категории ──────────────────────────────────────────────────────
function CategoryModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    category_id: item?.category_id ?? '',
    name: item?.name ?? '',
    last_article: item?.last_article ?? '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || form.category_id === '') return;
    setLoading(true);
    try {
      const data = { ...form, category_id: Number(form.category_id) };
      const res = item?.id
        ? await cheeseAPI.updateCategory(item.id, data)
        : await cheeseAPI.createCategory(data);
      onSave(res.data);
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CheeseModal
      title={item?.id ? 'Редактировать категорию' : 'Новая категория'}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      disabled={!form.name.trim() || form.category_id === ''}
    >
      <Field label="ID категории">
        <input className="mng-input" type="text" inputMode="decimal" value={form.category_id}
          onChange={e => set('category_id', e.target.value)} autoFocus />
      </Field>
      <Field label="Название">
        <input className="mng-input" value={form.name}
          onChange={e => set('name', e.target.value)} placeholder="Кроссовки…" />
      </Field>
      <Field label="Последний артикул">
        <input className="mng-input" value={form.last_article}
          onChange={e => set('last_article', e.target.value)} />
      </Field>
    </CheeseModal>
  );
}

// ── Вычисление диапазона размеров из sizes_qty ─────────────────────────────
function computeSizesRange(sizes_qty) {
  if (!sizes_qty || typeof sizes_qty !== 'object') return '';
  const nums = Object.entries(sizes_qty)
    .filter(([, qty]) => Number(qty) > 0)
    .map(([s]) => Number(s)).filter(n => !isNaN(n)).sort((a, b) => a - b);
  if (!nums.length) return '';
  return nums.length === 1 ? String(nums[0]) : `${nums[0]}-${nums[nums.length - 1]}`;
}

// Метки для полей шаблона
const TG_FIELD_LABELS = {
  name:                   'Название товара',
  code:                   'Код товара',
  catalog_sku:            'Артикул каталога',
  manufacturer:           'Производитель',
  materials:              'Материалы',
  sizes:                  'Размеры (детально)',
  sizes_range:            'Диапазон размеров',
  box:                    'Комплектация',
  market_price_avg:       'Цена дроп, ₴',
  purchase_price_per_pair:'Цена за пару, $',
  delivery_cost:          'Доставка, $',
  total_purchase_price:   'Закупка итого, $',
  supplier_article:       'Артикул поставщика',
};

// Поля вычисляются автоматически (не редактируются пользователем)
const TG_AUTO_FIELDS = new Set(['box', 'sizes', 'purchase_uah_total']);

// Начальные значения для полей из данных товара
function buildInitialValues(item) {
  const sizes = item.sizes_qty || {};
  const sizesStr = Object.entries(sizes).map(([s, q]) => `${s}×${q}`).join(' / ') || '';
  const pairs = Object.values(sizes).reduce((s, q) => s + Number(q), 0);
  const price = item.purchase_price_per_pair || 0;
  const rate  = item.exchange_rate || 0;
  const autoUah = (pairs > 0 && price > 0 && rate > 0) ? Math.round(pairs * price * rate) : (item.purchase_uah_total || '');
  return {
    name:                   item.name || '',
    code:                   item.code || item.supplier_article || '',
    catalog_sku:            item.catalog_sku || '',
    manufacturer:           item.manufacturer || '',
    materials:              item.materials || '',
    sizes:                  sizesStr,
    sizes_range:            item.sizes_range || computeSizesRange(sizes) || '',
    box:                    item.box_type === 'original' ? 'Оригінальна коробка 📦' : item.box_type === 'white' ? 'Біла коробка' : 'Брендова коробка',
    market_price_avg:       item.market_price_avg != null ? String(item.market_price_avg) : '',
    purchase_price_per_pair:item.purchase_price_per_pair != null ? String(item.purchase_price_per_pair) : '',
    purchase_uah_total:     autoUah != null ? String(autoUah) : '',
    delivery_cost:          item.delivery_cost != null ? String(item.delivery_cost) : '',
    total_purchase_price:   item.total_purchase_price != null ? String(item.total_purchase_price) : '',
    supplier_article:       item.supplier_article || '',
  };
}

// Рендер шаблона через словарь значений
function renderTgTemplate(tpl, values) {
  if (!tpl) return '';
  return Object.entries(values).reduce(
    (t, [k, v]) => t.replaceAll(`{${k}}`, v || ''),
    tpl
  );
}

// Поля которые сохраняются в transit при публикации
const TG_PATCHABLE = {
  code: v => v,
  sizes_range: v => v,
  market_price_avg: v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; },
  purchase_price_per_pair: v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; },
  delivery_cost: v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; },
  manufacturer: v => v,
  materials: v => v,
  catalog_sku: v => v,
  supplier_article: v => v,
};

// ── Модалка Telegram ───────────────────────────────────────────────────────
function TelegramModal({ item, onClose, onDone }) {
  const hasPost = !!item.tg_message_id;
  const [mode, setMode] = useState(hasPost ? 'edit' : 'publish');

  // Шаблон и значения полей
  const [tpl, setTpl] = useState('');
  const [values, setValues] = useState(() => buildInitialValues(item));
  const [tplLoading, setTplLoading] = useState(false);

  // Поля для edit/comment
  const [text, setText] = useState('');
  const [photoUrl, setPhotoUrl] = useState(item.tg_photo_url || '');
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);   // File[]
  const [captionLoading, setCaptionLoading] = useState(false);
  const [noDiscussionId, setNoDiscussionId] = useState(false);
  const [manualDiscId, setManualDiscId] = useState('');
  const [savingDiscId, setSavingDiscId] = useState(false);

  const [noCaption, setNoCaption] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Загружаем шаблон из конфига
  useEffect(() => {
    if (mode !== 'publish') return;
    setTplLoading(true);
    configAPI.getConfig()
      .then(res => {
        const cfg = res.data || [];
        const row = cfg.find(c => c.key === 'cheese_tg_template');
        setTpl(row?.value || '{name}');
      })
      .catch(() => setTpl('{name}'))
      .finally(() => setTplLoading(false));
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Загружаем рендеренный текст для edit-режима
  useEffect(() => {
    if (mode !== 'edit') return;
    setCaptionLoading(true);
    cheeseAPI.getCaption(item.id)
      .then(res => setText(res.data.text))
      .catch(() => {})
      .finally(() => setCaptionLoading(false));
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const setVal = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const setOk  = (t) => setMsg({ type: 'ok',  text: t });
  const setErr = (e) => {
    const detail = e?.response?.data?.detail;
    const text = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : (detail || e.message);
    setMsg({ type: 'err', text: 'Помилка: ' + text });
  };

  // Переменные из шаблона (уникальные, в порядке появления)
  const usedVars = [...new Set([...(tpl.matchAll(/\{(\w+)\}/g))].map(m => m[1]))];

  // Live preview
  const preview = renderTgTemplate(tpl, values);

  // Обязательные поля = все переменные из шаблона, кроме авто
  const missing = usedVars.filter(v => !TG_AUTO_FIELDS.has(v) && !String(values[v] || '').trim());
  const hasTgPhoto = !!photoUrl;
  const canPublish = missing.length === 0 && hasTgPhoto && !tplLoading && !loading;

  const handlePublish = async () => {
    setLoading(true); setMsg(null);
    try {
      // Сохраняем изменённые поля в товар
      const patch = {};
      for (const [key, transform] of Object.entries(TG_PATCHABLE)) {
        const newVal = transform(values[key] || '');
        if (newVal !== undefined && newVal !== (item[key] ?? '')) {
          patch[key] = newVal;
        }
      }
      if (photoUrl && photoUrl !== (item.tg_photo_url || '')) {
        patch.tg_photo_url = photoUrl;
      }
      if (Object.keys(patch).length > 0) {
        await cheeseAPI.updateTransit(item.id, patch);
      }
      await cheeseAPI.tgPublish(item.id, noCaption ? '' : preview, noCaption);
      setOk('Опубликовано в Telegram!');
      onDone();
    } catch (e) { setErr(e); } finally { setLoading(false); }
  };

  const handleEdit = async () => {
    setLoading(true); setMsg(null);
    try {
      await cheeseAPI.tgEdit(item.id, { text: text || undefined, photo_url: photoUrl || undefined });
      setOk('Пост обновлён');
    } catch (e) { setErr(e); } finally { setLoading(false); }
  };

  const _isNoDiscId = (e) => {
    const detail = e?.response?.data?.detail;
    return typeof detail === 'object' && detail?.code === 'no_discussion_id';
  };

  const handleComment = async () => {
    if (!commentText.trim() && commentFiles.length === 0) return;
    setNoDiscussionId(false);
    if (commentFiles.length === 0) {
      setLoading(true); setMsg(null);
      try {
        await cheeseAPI.tgComment(item.id, commentText, undefined);
        setOk('Коментар відправлено');
        setCommentText('');
      } catch (e) {
        if (_isNoDiscId(e)) { setNoDiscussionId(true); setMsg({ type: 'err', text: e.response.data.detail.message }); }
        else setErr(e);
      } finally { setLoading(false); }
      return;
    }
    setLoading(true); setMsg(null);
    try {
      await cheeseAPI.tgCommentFiles(item.id, commentText, commentFiles);
      setOk('Коментар з фото відправлено');
      setCommentText(''); setCommentFiles([]);
    } catch (e) {
      if (_isNoDiscId(e)) { setNoDiscussionId(true); setMsg({ type: 'err', text: e.response.data.detail.message }); }
      else setErr(e);
    } finally { setLoading(false); }
  };

  const handleSaveDiscIdAndComment = async () => {
    const discId = manualDiscId.trim();
    if (!discId) return;
    setSavingDiscId(true); setMsg(null);
    try {
      await cheeseAPI.updateTransit(item.id, { tg_discussion_msg_id: discId });
      if (commentFiles.length === 0) {
        await cheeseAPI.tgComment(item.id, commentText, undefined);
      } else {
        await cheeseAPI.tgCommentFiles(item.id, commentText, commentFiles);
      }
      setOk('Коментар відправлено!');
      setCommentText(''); setCommentFiles([]); setNoDiscussionId(false); setManualDiscId('');
      onDone();
    } catch (e) { setErr(e); } finally { setSavingDiscId(false); }
  };

  const modes = [
    { key: 'publish', label: 'Публикация' },
    ...(hasPost ? [
      { key: 'edit',    label: 'Изменить пост' },
      { key: 'comment', label: 'Комментарий' },
    ] : []),
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cheese-modal cheese-modal--tg" onClick={e => e.stopPropagation()}>
        <div className="cheese-modal-header">
          <div>
            <h3 className="cheese-modal-title">Telegram</h3>
            <div className="cheese-modal-subtitle">{item.name}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {modes.length > 1 && (
          <div className="cheese-tg-tabs">
            {modes.map(m => (
              <button key={m.key}
                className={`cheese-tg-tab${mode === m.key ? ' cheese-tg-tab--active' : ''}`}
                onClick={() => { setMode(m.key); setMsg(null); }}>
                {m.label}
              </button>
            ))}
          </div>
        )}

        <div className="cheese-modal-body">
          {mode === 'publish' && (
            <div className="tg-pub-layout">
              {/* Левая панель — поля из шаблона */}
              <div className="tg-pub-fields">
                <div className="tg-pub-section">Фото для публикации</div>
                <TgPhotoDropZone value={photoUrl} onChange={setPhotoUrl} />

                <div className="tg-pub-section" style={{ marginTop: 12 }}>Поля для публикации</div>

                {tplLoading
                  ? <div style={{ color: 'var(--lg-text-3)', fontSize: 13 }}>Загрузка шаблона…</div>
                  : usedVars.map(v => {
                    const isEmpty = !TG_AUTO_FIELDS.has(v) && !String(values[v] || '').trim();
                    return (
                      <div key={v} className={`mng-field${isEmpty ? ' mng-field--required' : ''}`}>
                        <label className="mng-label">
                          {TG_FIELD_LABELS[v] || v}
                          {!TG_AUTO_FIELDS.has(v) && <span className="req-star"> *</span>}
                        </label>
                        <input
                          className="mng-input"
                          value={values[v] || ''}
                          onChange={e => setVal(v, e.target.value)}
                          readOnly={TG_AUTO_FIELDS.has(v)}
                          style={TG_AUTO_FIELDS.has(v) ? { opacity: 0.6, cursor: 'default' } : {}}
                        />
                      </div>
                    );
                  })
                }

                {!hasTgPhoto && (
                  <div className="tg-pub-missing">
                    Заполните «Фото для публикации в TG» перед публикацией
                  </div>
                )}

                {missing.length > 0 && (
                  <div className="tg-pub-missing">
                    Заполните: {missing.map(v => TG_FIELD_LABELS[v] || v).join(', ')}
                  </div>
                )}

                <div className="tg-pub-options">
                  <label className="tg-pub-option-label">
                    <input type="checkbox" checked={noCaption}
                      onChange={e => setNoCaption(e.target.checked)} />
                    Без подписи (только фото)
                  </label>
                </div>

                <button className="btn btn-primary tg-pub-btn"
                  onClick={handlePublish} disabled={!canPublish}>
                  {loading ? 'Публикация…' : '✈ Опубликовать'}
                </button>
              </div>

              {/* Правая панель — превью */}
              <div className="tg-pub-preview">
                <div className="tg-pub-section">Превью поста</div>
                <textarea className="mng-input tg-preview-textarea"
                  value={preview}
                  readOnly
                  rows={20}
                  style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }} />
              </div>
            </div>
          )}

          {mode === 'edit' && (
            <>
              <Field label="Текст поста">
                {captionLoading
                  ? <div className="cheese-caption-loading">Загрузка текста…</div>
                  : <textarea className="mng-input cheese-textarea" rows={10} value={text}
                      onChange={e => setText(e.target.value)} placeholder="Текст поста…"
                      style={{ fontFamily: 'monospace', fontSize: 12 }} />
                }
              </Field>
              <Field label="Фото поста (ссылка)">
                <input className="mng-input" value={photoUrl}
                  onChange={e => setPhotoUrl(e.target.value)} placeholder="https://…" />
              </Field>
              {photoUrl && (
                <div className="cheese-edit-photo-preview">
                  <img
                    src={proxyImageUrl(photoUrl)}
                    alt="Превью"
                    className="cheese-edit-photo-img"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              <button className="btn btn-primary" onClick={handleEdit} disabled={loading || captionLoading}>
                {loading ? 'Обновление…' : 'Обновить пост'}
              </button>
            </>
          )}

          {mode === 'comment' && (
            <>
              <Field label="Текст коментаря">
                <textarea className="mng-input cheese-textarea" rows={4} value={commentText}
                  onChange={e => setCommentText(e.target.value)} placeholder="Текст…" />
              </Field>
              <CommentFileDropZone files={commentFiles} onChange={setCommentFiles} />
              <button className="btn btn-primary" onClick={handleComment}
                disabled={loading || (!commentText.trim() && commentFiles.length === 0)}>
                {loading ? 'Відправка…' : 'Відправити'}
              </button>
              {noDiscussionId && (
                <div className="cheese-no-disc-wrap">
                  <div className="cheese-no-disc-title">Як знайти ID обговорення:</div>
                  <ol className="cheese-no-disc-steps">
                    {item.tg_post_url && (
                      <li>Відкрийте <a href={item.tg_post_url} target="_blank" rel="noreferrer">пост у Telegram</a></li>
                    )}
                    <li>Натисніть «Comments» під постом</li>
                    <li>Скопіюйте посилання на перший пост у групі обговорень</li>
                    <li>Останнє число у URL — це ID (наприклад: t.me/c/123456/<strong>789</strong>)</li>
                  </ol>
                  <div className="cheese-no-disc-row">
                    <input
                      className="mng-input cheese-no-disc-input"
                      type="number"
                      placeholder="ID обговорення"
                      value={manualDiscId}
                      onChange={e => setManualDiscId(e.target.value)}
                    />
                    <button className="btn btn-primary" onClick={handleSaveDiscIdAndComment}
                      disabled={!manualDiscId.trim() || savingDiscId}>
                      {savingDiscId ? 'Збереження…' : 'Зберегти та надіслати'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {msg && (
            <div className={`cheese-msg${msg.type === 'err' ? ' cheese-msg--err' : ' cheese-msg--ok'}`}>
              {msg.text}
            </div>
          )}
        </div>

        <div className="cheese-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}


// ── Inline-сетка размеров для карточки ─────────────────────────────────────

const ALL_SIZES_FIXED = ['35','36','37','38','39','40','41','42','43','44','45','46','47'];

function SizeCellInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  useEffect(() => { setVal(value ?? ''); }, [value]);
  const save = () => {
    setEditing(false);
    const n = parseInt(val, 10) || 0;
    if (n !== (value || 0)) onSave(n || null);
  };
  if (editing) return (
    <input autoFocus type="text" inputMode="decimal" min="0" value={val}
      className="cheese-sizes-cell-input"
      onChange={e => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); } }}
    />
  );
  return (
    <span className={`cheese-sizes-cell-qty${!value ? ' cheese-sizes-cell-empty' : ''}`}
      onClick={() => setEditing(true)}>
      {value || '—'}
    </span>
  );
}

function SizesInlineGrid({ sizes_qty, onPatchSize }) {
  return (
    <div className="cheese-sizes-grid">
      {ALL_SIZES_FIXED.map(s => {
        const qty = Number(sizes_qty?.[s]) || 0;
        return (
          <div key={s} className={`cheese-sizes-cell${qty > 0 ? ' cheese-sizes-cell--has' : ''}`}>
            <span className="cheese-sizes-cell-label">{s}</span>
            <SizeCellInput value={qty || null} onSave={v => onPatchSize(s, v)} />
          </div>
        );
      })}
    </div>
  );
}

// ── Карточка заказа ────────────────────────────────────────────────────────
function OrderCard({ order, onEdit, onToTransit, onDelete, onPatchSize, onResetExport }) {
  const pairs = totalPairs(order.sizes_qty);
  return (
    <div className={`cheese-card cheese-card--with-sizes${order.exported_at ? ' cheese-card--exported' : ''}`}>
      <CheesePhoto url={order.photo_url} size={64} />
      <div className="cheese-card-body">
        <div className="cheese-card-name">
          {order.name}
          {order.box_type === 'original' && <span className="cheese-badge cheese-badge--box" style={{ marginLeft: 6 }}>BOX</span>}
        </div>
        <div className="cheese-card-meta">
          <span>{pairs} {pairsWord(pairs)}</span>
          {order.exported_at && <span className="cheese-badge cheese-badge--exported">✓ выгружено</span>}
        </div>
        <div className="cheese-card-sizes-row">
          <SizesInlineGrid sizes_qty={order.sizes_qty} onPatchSize={onPatchSize} />
          <div className="cheese-card-actions">
            {order.exported_at && (
              <button className="cheese-icon-btn cheese-icon-btn--reset" onClick={() => onResetExport(order.id)} title="Снять отметку о выгрузке">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                  <path d="M3 3v5h5"/>
                </svg>
              </button>
            )}
            <button className="cheese-icon-btn" onClick={() => onEdit(order)} title="Редактировать">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button className="cheese-icon-btn cheese-icon-btn--transit" onClick={() => onToTransit(order.id)} title="Отправить в путь">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
            <button className="cheese-icon-btn cheese-icon-btn--del" onClick={() => onDelete(order.id)} title="Удалить">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Карточка товара в пути ─────────────────────────────────────────────────
// ── Inline-редактируемая ячейка ────────────────────────────────────────────
function InlineCell({ value, onSave, type = 'number', placeholder = '—', suffix = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  useEffect(() => { setVal(value ?? ''); }, [value]);
  const save = () => {
    setEditing(false);
    let parsed;
    if (type === 'number') {
      const num = parseFloat(val);
      parsed = val !== '' && !isNaN(num) ? num : null;
    } else {
      parsed = val || null;
    }
    if (parsed !== (value ?? null)) onSave(parsed);
  };
  if (editing) {
    return (
      <input autoFocus type={type} value={val}
        className="cheese-tr-inline-input"
        onChange={e => setVal(type === 'number' ? e.target.value.replace(',', '.') : e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); } }}
      />
    );
  }
  return (
    <span className={`cheese-tr-inline-val${!value ? ' cheese-tr-inline-empty' : ''}`} onClick={() => setEditing(true)}>
      {value != null && value !== '' ? `${value}${suffix}` : placeholder}
    </span>
  );
}

// ── Строка таблицы "В пути" ────────────────────────────────────────────────
function TransitRow({ item, allSizes, rate, kgPrice, onEdit, onTg, onDelete, onPatch, onNextStatus, onEasydrop, checked, onCheck, onZoom }) {
  const pairs = totalPairs(item.sizes_qty);
  const opt$  = item.purchase_price_per_pair || 0;
  const itemRate = item.exchange_rate || rate; // приоритет — сохранённый курс, fallback — глобальный
  const optUah = opt$ * itemRate;
  const wKg  = item.weight_kg || 0;
  const boxUah = item.boxes_delivery_uah || 0;
  const del$ = pairs > 0 ? (wKg * kgPrice + (wKg * 20) / 100 * 3) / pairs : 0;
  const rawOptDel = del$ * itemRate + optUah + boxUah;
  const optDel = rawOptDel > 0 ? Math.ceil((rawOptDel + 100) / 10) * 10 : 0;
  const market = item.market_price_avg || 0;
  const margin = market > 0 && optDel > 0 ? market - optDel : null;
  const buy$ = (del$ + opt$ + (boxUah / (itemRate || 1))) * pairs;
  const buyUah = buy$ * itemRate;
  const noDel$ = opt$ * pairs;
  const delTotal$ = del$ * pairs;

  // ±Прибытие = china_ship_date + 30 дней
  let arrival = '—';
  if (item.china_ship_date) {
    const d = new Date(item.china_ship_date);
    d.setDate(d.getDate() + 30);
    arrival = d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }

  const patch = (field, value) => onPatch(item.id, { [field]: value });
  const fmt2 = (n) => n != null && n > 0 ? n.toFixed(2) : '—';
  const fmtN = (n) => n != null && n > 0 ? Math.round(n).toLocaleString('ru-RU') : '—';

  return (
    <tr className={`${item.status === 'received' ? 'cheese-tr--received' : ''}${checked ? ' cheese-tr-selected' : ''}`}>
      {onCheck !== undefined && (
        <td className="cheese-tr-check"><input type="checkbox" checked={!!checked} onChange={() => onCheck(item.id)} /></td>
      )}
      <td className="cheese-tr-photo cg-id cg-sticky-1"
        style={onZoom && (item.tg_photo_url || item.photo_url) ? { cursor: 'zoom-in' } : undefined}
        onClick={onZoom && (item.tg_photo_url || item.photo_url) ? () => onZoom(item.tg_photo_url || item.photo_url) : undefined}>
        <CheesePhoto url={item.photo_url} size={64} />
      </td>
      <td className="cheese-tr-name-cell cg-id cg-sticky-2">
        <div className="cheese-tr-name">
          {item.name}
          {item.box_type === 'original' && <span className="cheese-badge cheese-badge--box" style={{ marginLeft: 6 }}>BOX</span>}
          {item.box_type === 'white' && <span className="cheese-badge cheese-badge--whitebox" style={{ marginLeft: 6 }}>Біла кор.</span>}
        </div>
        {item.supplier_article && <div className="cheese-tr-sup-art">{item.supplier_article}</div>}
        <div className="cheese-tr-badges">
          <span className={`cheese-badge cheese-badge--${item.status}`}>{item.status === 'transit' ? 'В пути' : 'Получен'}</span>
          {item.tg_post_url
            ? <a className="cheese-badge cheese-badge--tg" href={item.tg_post_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>TG ↗</a>
            : item.tg_message_id && <span className="cheese-badge cheese-badge--tg">TG</span>
          }
          {item.notes && <span className="cheese-badge cheese-badge--note" title={item.notes}>📝</span>}
        </div>
      </td>
      <td className="cheese-tr-center cg-id cg-sticky-3">{arrival}</td>
      <td className="cheese-tr-center cg-id cg-sticky-4">
        {item.catalog_sku
          ? <button className="cheese-sku-link" onClick={() => window.dispatchEvent(new CustomEvent('cheese-open-catalog', { detail: { sku: item.catalog_sku } }))}>{item.catalog_sku}</button>
          : '—'}
      </td>
      <td className="cheese-tr-num cg-price">{opt$ > 0 ? `${opt$} $` : '—'}</td>
      <td className="cheese-tr-num cg-price">{optUah > 0 ? `${Math.round(optUah).toLocaleString('ru-RU')} ₴` : '—'}</td>
      <td className="cheese-tr-editable cg-log">
        <div className="cheese-weight-cell">
          <InlineCell value={item.weight_kg} onSave={v => patch('weight_kg', v)} placeholder="—" />
          <button
            className={`cheese-weight-btn ${item.weight_confirmed ? 'cheese-weight-btn--ok' : 'cheese-weight-btn--approx'}`}
            onClick={() => patch('weight_confirmed', !item.weight_confirmed)}
            title={item.weight_confirmed ? 'Вес подтверждён — нажми чтобы снять' : 'Вес примерный — нажми чтобы подтвердить'}
          >
            {item.weight_confirmed ? '✓' : '~'}
          </button>
        </div>
      </td>
      <td className={`cheese-tr-num cg-log${!item.weight_confirmed ? ' cheese-tr-approx' : ''}`}>{fmt2(del$)} {del$ > 0 ? '$' : ''}</td>
      <td className="cheese-tr-editable cg-log"><InlineCell value={item.boxes_delivery_uah} onSave={v => patch('boxes_delivery_uah', v)} placeholder="—" /></td>
      <td className={`cheese-tr-num cg-log${!item.weight_confirmed ? ' cheese-tr-approx' : ''}`}>{optDel > 0 ? `${optDel.toLocaleString('ru-RU')} ₴` : '—'}</td>
      <td className="cheese-tr-editable cg-market"><InlineCell value={item.market_price_avg} onSave={v => patch('market_price_avg', v)} placeholder="—" /></td>
      <td className={`cheese-tr-num cg-market${!item.weight_confirmed ? ' cheese-tr-approx' : ''}${margin != null ? (margin > 0 ? ' cheese-tr-pos' : ' cheese-tr-neg') : ''}`}>
        {margin != null ? `${Math.round(margin).toLocaleString('ru-RU')} ₴` : '—'}
      </td>
      <td className={`cheese-tr-num cg-market${!item.weight_confirmed ? ' cheese-tr-approx' : ''}`}>
        {optDel > 0 && itemRate > 0 ? `${(optDel / itemRate).toFixed(2)} $` : '—'}
      </td>
      {allSizes.map(s => (
        <td key={s} className={`cheese-tr-size cheese-tr-editable cg-size ${(item.sizes_qty?.[s] || 0) > 0 ? 'cheese-tr-size--has' : 'cheese-tr-size--empty'}`}>
          <InlineCell
            value={Number(item.sizes_qty?.[s]) || null}
            placeholder="—"
            onSave={v => {
              const newSizes = { ...(item.sizes_qty || {}) };
              if (v && v > 0) newSizes[s] = v;
              else delete newSizes[s];
              patch('sizes_qty', newSizes);
            }}
          />
        </td>
      ))}
      <td className="cheese-tr-num cg-total"><b>{pairs || '—'}</b></td>
      <td className={`cheese-tr-num cg-total${!item.weight_confirmed ? ' cheese-tr-approx' : ''}`}>{fmtN(buyUah)} {buyUah > 0 ? '₴' : ''}</td>
      <td className={`cheese-tr-num cg-total${!item.weight_confirmed ? ' cheese-tr-approx' : ''}`}>{fmtN(buy$)} {buy$ > 0 ? '$' : ''}</td>
      <td className="cheese-tr-num cg-total">{fmtN(noDel$)} {noDel$ > 0 ? '$' : ''}</td>
      <td className={`cheese-tr-num cg-total${!item.weight_confirmed ? ' cheese-tr-approx' : ''}`}>{fmtN(delTotal$)} {delTotal$ > 0 ? '$' : ''}</td>
      <td className="cheese-tr-editable cg-dates"><InlineCell value={item.order_date} type="date" onSave={v => patch('order_date', v)} placeholder="—" /></td>
      <td className="cheese-tr-editable cg-dates"><InlineCell value={item.china_ship_date} type="date" onSave={v => patch('china_ship_date', v)} placeholder="—" /></td>
      <td className="cheese-tr-actions-cell">
        <button className="cheese-icon-btn" onClick={() => onEdit(item)} title="Редактировать">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        {onTg && (
          <button className="cheese-icon-btn cheese-icon-btn--tg" onClick={() => onTg(item)} title="Telegram">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.247l-2.01 9.47c-.148.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.462c.537-.194 1.006.131.873.752z"/></svg>
          </button>
        )}
        {onNextStatus && (
          <button className="cheese-icon-btn cheese-icon-btn--next" onClick={() => onNextStatus(item.id)}
            title={item.status === 'transit' ? 'Отметить как Получено' : 'Отметить как Опубликовано'}>
            {item.status === 'transit'
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            }
          </button>
        )}
        {onEasydrop && (
          <button className={`cheese-icon-btn${item.easydrop_item_id ? ' cheese-icon-btn--easydrop-linked' : ''}`}
            onClick={() => onEasydrop(item)}
            title={item.easydrop_item_id
              ? `Оновити розміри в EasyDrop (id ${item.easydrop_item_id})`
              : 'Опублікувати новий товар в EasyDrop'}>
            {item.easydrop_item_id
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></svg>
            }
          </button>
        )}
        <button className="cheese-icon-btn cheese-icon-btn--del" onClick={() => onDelete(item.id)} title="Удалить">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
        </button>
      </td>
    </tr>
  );
}

function pairsWord(n) {
  if (n % 10 === 1 && n % 100 !== 11) return 'пара';
  if ([2,3,4].includes(n % 10) && ![12,13,14].includes(n % 100)) return 'пары';
  return 'пар';
}

// ── Модальное окно перевода в "В пути" ────────────────────────────────────
function TransferModal({ order, categories, onConfirm, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: order?.name || '',
    photo_url: order?.photo_url || '',
    sizes_qty: order?.sizes_qty || {},
    has_box: order?.has_box || false,
    box_type: order?.box_type || (order?.has_box ? 'original' : 'brand'),
    purchase_price_per_pair: '',
    exchange_rate: '',
    materials: '',
    manufacturer: "В'єтнам",
    order_date: order?.created_at ? order.created_at.slice(0, 10) : '',
    china_ship_date: today,
    category_id_val: '',
    category_name: '',
    supplier_article: '',
    supplier_name: '',
    item_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [prevData, setPrevData] = useState(null);
  const [prevApplied, setPrevApplied] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyPrev = (data) => {
    if (!data) return;
    setForm(f => ({
      ...f,
      manufacturer:    data.manufacturer    || f.manufacturer,
      category_name:   data.category_name   || f.category_name,
      category_id_val: data.category_id_val != null ? data.category_id_val : f.category_id_val,
      supplier_article: data.supplier_article || f.supplier_article,
      supplier_name:   data.supplier_name   || f.supplier_name,
      item_id:         data.item_id != null  ? data.item_id  : f.item_id,
      materials:       data.materials        || f.materials,
    }));
    setPrevApplied(true);
  };

  useEffect(() => {
    const sku = order?.catalog_sku;
    const prevPromise = sku
      ? cheeseAPI.getPrevTransit(sku).then(r => r.data).catch(() => null)
      : Promise.resolve(null);
    const cfgPromise = configAPI.getConfig().catch(() => ({ data: [] }));
    Promise.all([prevPromise, cfgPromise]).then(([prev, cfg]) => {
      // Автоматично переносимо параметри повторного замовлення — без ручного кліку
      if (prev) { setPrevData(prev); applyPrev(prev); }
      const byKey = Object.fromEntries((cfg.data || []).map(c => [c.key, c.value]));
      if (byKey.exchange_rate) setForm(f => ({ ...f, exchange_rate: f.exchange_rate || byKey.exchange_rate }));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryNameChange = (e) => {
    const name = e.target.value;
    const cat = categories.find(c => c.name === name);
    setForm(f => ({ ...f, category_name: name, category_id_val: cat ? cat.category_id : f.category_id_val }));
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const body = {
        name:            form.name || null,
        photo_url:       form.photo_url || null,
        sizes_qty:       form.sizes_qty || null,
        has_box:         form.box_type === 'original',
        box_type:        form.box_type || 'brand',
        purchase_price_per_pair: form.purchase_price_per_pair ? parseFloat(form.purchase_price_per_pair) : null,
        exchange_rate:   form.exchange_rate ? parseFloat(form.exchange_rate) : null,
        materials:       form.materials || null,
        manufacturer:    form.manufacturer || "В'єтнам",
        order_date:      form.order_date || null,
        china_ship_date: form.china_ship_date || null,
        category_id_val: form.category_id_val ? parseInt(form.category_id_val) : null,
        category_name:   form.category_name || null,
        supplier_article: form.supplier_article || null,
        supplier_name:   form.supplier_name || null,
        item_id:         form.item_id ? parseInt(form.item_id) : null,
      };
      await onConfirm(body);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mng-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="mng-modal transfer-modal">
        <div className="mng-modal-header">
          <h3 className="mng-modal-title">Перевод в «В пути»</h3>
          <button className="mng-modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="mng-modal-body" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>

          {prevApplied && prevData && (
            <div className="transfer-prev-applied">
              Параметры перенесены автоматически из предыдущего заказа: <b>{prevData.category_name || '—'}</b>
              {prevData.manufacturer ? `, ${prevData.manufacturer}` : ''}
              {prevData.supplier_name ? `, у «${prevData.supplier_name}»` : ''}
            </div>
          )}

          <div className="mng-field">
            <label className="mng-label">Название товара</label>
            <input className="mng-input" value={form.name}
              onChange={e => set('name', e.target.value)} />
          </div>

          <div className="mng-field">
            <label className="mng-label">Ссылка на фото</label>
            <input className="mng-input" value={form.photo_url}
              onChange={e => set('photo_url', e.target.value)} placeholder="https://…" />
          </div>

          <div className="mng-field">
            <label className="mng-label">Размеры и количество</label>
            <SizesEditor value={form.sizes_qty} onChange={v => set('sizes_qty', v)} allSizes={!order} />
          </div>

          <div className="mng-field" style={{ marginBottom: 4 }}>
            <label className="mng-label">Коробка / упаковка</label>
            <select className="mng-input" value={form.box_type || 'brand'}
              onChange={e => { const bt = e.target.value; setForm(f => ({ ...f, box_type: bt, has_box: bt === 'original' })); }}>
              <option value="original">Оригінальна коробка 📦</option>
              <option value="brand">Брендова коробка</option>
              <option value="white">Біла коробка</option>
            </select>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--lg-border)', margin: '8px 0' }} />

          <div className="mng-field-row">
            <div className="mng-field">
              <label className="mng-label">Опт / пара, $</label>
              <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.01"
                value={form.purchase_price_per_pair}
                onChange={e => set('purchase_price_per_pair', e.target.value.replace(',', '.'))}
                placeholder="0.00" />
            </div>
            <div className="mng-field">
              <label className="mng-label">Курс $ → ₴</label>
              <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.5"
                value={form.exchange_rate}
                onChange={e => set('exchange_rate', e.target.value.replace(',', '.'))}
                placeholder="46" />
            </div>
          </div>
          <div className="mng-field">
            <label className="mng-label">Країна виробництва</label>
            <select className="mng-input" value={form.manufacturer || "В'єтнам"}
              onChange={e => set('manufacturer', e.target.value)}>
              <option value="В'єтнам">В'єтнам</option>
              <option value="Китай">Китай</option>
            </select>
          </div>

          <div className="mng-field">
            <label className="mng-label">Матеріали</label>
            <MaterialsPicker value={form.materials} onChange={v => set('materials', v)} />
          </div>

          <div className="mng-field-row">
            <div className="mng-field">
              <label className="mng-label">Дата заказа</label>
              <input className="mng-input" type="date" value={form.order_date}
                onChange={e => set('order_date', e.target.value)} />
            </div>
            <div className="mng-field">
              <label className="mng-label">Отправка из Китая</label>
              <input className="mng-input" type="date" value={form.china_ship_date}
                onChange={e => set('china_ship_date', e.target.value)} />
            </div>
          </div>

          <div className="mng-field-row">
            <div className="mng-field" style={{ flex: 2 }}>
              <label className="mng-label">Название категории</label>
              <select className="mng-input" value={form.category_name} onChange={handleCategoryNameChange}>
                <option value="">— выбрать —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="mng-field">
              <label className="mng-label">Код категории</label>
              <input className="mng-input" type="text" inputMode="decimal"
                value={form.category_id_val}
                readOnly style={{ opacity: 0.7, cursor: 'default' }}
                placeholder="—" />
            </div>
          </div>

          <div className="mng-field">
            <label className="mng-label">Артикул поставщика</label>
            <input className="mng-input" value={form.supplier_article}
              onChange={e => set('supplier_article', e.target.value)} placeholder="NK158…" />
          </div>
          </div>{/* end left column */}
          <div className="rcv-sup-panel">
            <div className="rcv-sup-title">Наличие у поставщиков</div>
            <SupplierPanel catalogSku={form.catalog_sku || order?.catalog_sku} />
          </div>
        </div>
        <div className="mng-modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Отмена</button>
          <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Перевод…' : '→ В пути'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Вкладка Закупка ────────────────────────────────────────────────────────
function OrdersSubtab({ search = '' }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [transferOrder, setTransferOrder] = useState(null); // order being moved to transit

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try { const res = await cheeseAPI.listOrders(); setOrders(res.data); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    cheeseAPI.listCategories().then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleSave = (saved) => setOrders(prev => {
    const idx = prev.findIndex(o => o.id === saved.id);
    if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n; }
    return [saved, ...prev];
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить позицию из закупки?')) return;
    await cheeseAPI.deleteOrder(id);
    setOrders(prev => prev.filter(o => o.id !== id));
  };

  const handleToTransit = (id) => {
    const order = orders.find(o => o.id === id);
    if (order) setTransferOrder(order);
  };

  const handleTransferConfirm = async (body) => {
    try {
      await cheeseAPI.orderToTransit(transferOrder.id, body);
      setOrders(prev => prev.filter(o => o.id !== transferOrder.id));
      setTransferOrder(null);
    } catch (e) { alert('Ошибка: ' + (e?.response?.data?.detail || e.message)); }
  };

  const handleResetExport = async (id) => {
    try {
      const res = await cheeseAPI.resetOrderExport(id);
      handleSave(res.data);
    } catch (e) { alert('Ошибка: ' + (e?.response?.data?.detail || e.message)); }
  };

  const handlePatchOrderSize = async (orderId, currentSizes, size, newQty) => {
    const newSizes = { ...(currentSizes || {}) };
    if (newQty && newQty > 0) newSizes[size] = newQty;
    else delete newSizes[size];
    try {
      const res = await cheeseAPI.updateOrder(orderId, { sizes_qty: newSizes });
      handleSave(res.data);
    } catch { /* silent */ }
  };

  const GSHEET_URL = 'https://docs.google.com/spreadsheets/d/1EaneKDVA39LNZHVyHOiffdQvfms8v-N2tdDUL7n8IZE/edit';

  const [exporting, setExporting] = useState(false);
  const [sendingTg, setSendingTg] = useState(false);

  const handleSendTg = async () => {
    setSendingTg(true);
    try {
      await cheeseAPI.sendOrdersTg();
      alert('Файл отправлен в Telegram ✓');
      await load();
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSendingTg(false);
    }
  };

  const handleDownloadXlsx = async () => {
    if (!window.confirm('Записать закупку в Google Sheets и скачать XLSX?')) return;
    setExporting(true);
    try {
      const res = await cheeseAPI.exportOrdersGSheet();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename\*?=(?:UTF-8'')?([^;\n]+)/i);
      a.download = match ? decodeURIComponent(match[1].replace(/"/g, '')) : 'zakupka.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      await load();
    } catch (e) {
      alert('Ошибка экспорта: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visibleOrders = q
    ? orders.filter(o =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.catalog_sku || '').toLowerCase().includes(q) ||
        (o.supplier_article || '').toLowerCase().includes(q)
      )
    : orders;
  const unexportedOrders = orders.filter(o => !o.exported_at);
  const totalPairsAll = orders.reduce((s, o) => s + totalPairs(o.sizes_qty), 0);

  return (
    <div className="cheese-subtab">
      {loadError && <div className="cheese-load-error">Ошибка загрузки. <button className="btn btn-sm btn-secondary" onClick={load}>Повторить</button></div>}
      <div className="cheese-toolbar">
        <button className="btn btn-primary btn-sm" onClick={() => setModal('create')}>
          + Добавить позицию
        </button>
        <a className="btn btn-secondary btn-sm" href={GSHEET_URL} target="_blank" rel="noreferrer">
          ↗ Google Sheets
        </a>
        <button className="btn btn-secondary btn-sm" onClick={handleDownloadXlsx} disabled={exporting}>
          {exporting ? 'Сохранение…' : '↓ Скачать XLS'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={handleSendTg} disabled={sendingTg}>
          {sendingTg ? 'Отправка…' : '✈ в Telegram'}
        </button>
        <div className="cheese-toolbar-stats">
          <span>{visibleOrders.length}{q ? `/${orders.length}` : ''} позиций</span>
          {totalPairsAll > 0 && <span>·</span>}
          {totalPairsAll > 0 && <span>{totalPairsAll} {pairsWord(totalPairsAll)}</span>}
          {unexportedOrders.length < orders.length && (
            <span className="cheese-stats-unexported">· {unexportedOrders.length} невыгруж.</span>
          )}
        </div>
      </div>

      {loading && <div className="cheese-empty">Загрузка…</div>}
      {!loading && orders.length === 0 && (
        <div className="cheese-empty">
          <div className="cheese-empty-icon">📋</div>
          <div>Закупка пуста</div>
          <div className="cheese-empty-hint">Добавьте товары вручную или из каталога</div>
        </div>
      )}

      {!loading && orders.length > 0 && visibleOrders.length === 0 && (
        <div className="cheese-empty">Ничего не найдено по запросу «{search}»</div>
      )}
      <div className="cheese-list">
        {!loading && visibleOrders.map(order => (
          <OrderCard key={order.id} order={order}
            onEdit={setModal} onToTransit={handleToTransit} onDelete={handleDelete}
            onResetExport={handleResetExport}
            onPatchSize={(size, qty) => handlePatchOrderSize(order.id, order.sizes_qty, size, qty)} />
        ))}
      </div>

      {modal && (
        <OrderModal
          item={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
      {transferOrder && (
        <TransferModal
          order={transferOrder}
          categories={categories}
          onConfirm={handleTransferConfirm}
          onCancel={() => setTransferOrder(null)}
        />
      )}
    </div>
  );
}

// ── Хелпер инкремента артикула ─────────────────────────────────────────────
function incrementArticle(last) {
  if (!last) return '';
  const s = String(last);
  const m = s.match(/^(.*?)(\d+)$/);
  if (!m) return s;
  const next = String(parseInt(m[2]) + 1).padStart(m[2].length, '0');
  return m[1] + next;
}

function articleNumSuffix(art) {
  if (!art) return -1;
  const m = String(art).match(/(\d+)$/);
  return m ? parseInt(m[1]) : -1;
}

// ── Панель наличия у поставщиков + история продаж ──────────────────────────
function SupplierPanel({ catalogSku }) {
  const [suppliers, setSuppliers] = useState([]);
  const [salesHistory, setSalesHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!catalogSku) return;
    let cancelled = false;
    setLoading(true);
    setSuppliers([]);
    setSalesHistory(null);

    const run = async () => {
      try {
        const bySkuRes = await productsAPI.getBySku(catalogSku);
        if (cancelled) return;
        const prod = Array.isArray(bySkuRes.data) ? bySkuRes.data[0] : bySkuRes.data;

        let found = [];
        if (prod?.id) {
          const suppRes = await productsAPI.getSuppliers(prod.id);
          if (cancelled) return;
          found = suppRes.data || [];
        }
        setSuppliers(found);

        if (found.length === 0) {
          const histRes = await cheeseAPI.getSalesHistory(catalogSku);
          if (cancelled) return;
          setSalesHistory(histRes.data);
        }
      } catch (_) { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };

    run();
    return () => { cancelled = true; };
  }, [catalogSku]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!catalogSku) return <div className="rcv-sup-empty">Нет артикула каталога</div>;
  if (loading)    return <div className="rcv-sup-empty">Загрузка…</div>;

  if (suppliers.length > 0) {
    return (
      <div className="rcv-sup-list">
        {suppliers.map((s, i) => {
          const sizes = s.sizes
            ? s.sizes.split(' ').map(p => { const [sz, q] = p.split(':'); return { sz, q: Number(q) }; }).filter(x => x.q > 0)
            : [];
          return (
            <div key={i} className="rcv-sup-card">
              <div className="rcv-sup-header">
                <span className="rcv-sup-name">{s.supplier_name}</span>
                {s.price_numeric > 0 && <span className="rcv-sup-price">{s.price_numeric} ₴</span>}
              </div>
              {sizes.length > 0 && (
                <div className="rcv-sup-sizes">
                  {sizes.map(({ sz, q }) => (
                    <span key={sz} className="rcv-sup-size">{sz}<span className="rcv-sup-qty">×{q}</span></span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Нет наличия — показываем историю продаж
  return (
    <div className="rcv-sup-list">
      <div className="rcv-sup-empty">Нет в наличии у поставщиков</div>
      {salesHistory?.available && salesHistory.suppliers?.length > 0 && (
        <div className="rcv-sales-history">
          <div className="rcv-sales-title">Цены поставщиков</div>
          <div className="rcv-sales-rows">
            {salesHistory.suppliers.map((s, i) => (
              <div key={i} className={`rcv-sales-row${s.in_stock ? ' rcv-sales-row--instock' : ''}`}>
                <span className="rcv-sales-label rcv-sales-supplier">
                  {s.in_stock && <span className="rcv-instock-dot" title="Есть в наличии" />}
                  {s.name}
                </span>
                <span className="rcv-sales-val rcv-sales-val--accent">{s.price.toLocaleString('ru-RU')} ₴</span>
              </div>
            ))}
            {(salesHistory.sales_30 > 0 || salesHistory.sales_90 > 0) && (
              <>
                <div className="rcv-sales-divider" />
                <div className="rcv-sales-row rcv-sales-row--dim">
                  <span className="rcv-sales-label">Продажи 30 / 90 дн.</span>
                  <span className="rcv-sales-val" style={{ fontSize: 12 }}>
                    {salesHistory.sales_30} / {salesHistory.sales_90} шт.
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {salesHistory?.available && !salesHistory.suppliers?.length && (
        <div className="rcv-sup-empty" style={{ marginTop: 8 }}>Нет данных о ценах</div>
      )}
    </div>
  );
}

// ── Модал "Оформление получения" ────────────────────────────────────────────
function ReceiveModal({ item, batches = [], onClose, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name:                    item?.name || '',
    photo_url:               item?.photo_url || '',
    catalog_sku:             item?.catalog_sku || '',
    supplier_article:        item?.supplier_article || '',
    item_id:                 item?.item_id || '',
    received_at:             item?.received_at || today,
    sizes_qty:               item?.sizes_qty || {},
    has_box:                 item?.has_box || false,
    box_type:                item?.box_type || (item?.has_box ? 'original' : 'brand'),
    purchase_price_per_pair: item?.purchase_price_per_pair || '',
    materials:               item?.materials || '',
    manufacturer:            item?.manufacturer || "В'єтнам",
    weight_kg:               item?.weight_kg || '',
    order_date:              item?.order_date || '',
    china_ship_date:         item?.china_ship_date || '',
    category_name:           item?.category_name || '',
    category_id_val:         item?.category_id_val || '',
    market_price_avg:        item?.market_price_avg || '',
    boxes_delivery_uah:      item?.boxes_delivery_uah || '',
    code:                    item?.code || item?.supplier_article || '',
    sizes_range:             item?.sizes_range || computeSizesRange(item?.sizes_qty || {}),
    exchange_rate:           item?.exchange_rate || '',
    batch_id:                item?.batch_id || '',
  });
  const [category, setCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [kgPrice, setKgPrice] = useState(18);
  const [defaultRate, setDefaultRate] = useState(46);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      item?.item_id ? Promise.resolve(null) : cheeseAPI.getNextItemId(),
      cheeseAPI.listCategories(),
    ]).then(([idRes, catRes]) => {
      const cats = catRes.data || [];
      const cat = cats.find(c => c.category_id === item?.category_id_val) || null;
      setCategory(cat);
      setForm(f => {
        const newArt = f.supplier_article || (cat ? incrementArticle(cat.last_article) : '');
        return {
          ...f,
          item_id: idRes ? idRes.data.next_item_id : f.item_id,
          supplier_article: newArt,
          code: f.code || newArt,
        };
      });
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Загружаем курс и цену кг — всегда, чтобы kgPrice брался из настроек, а не из дефолта
  useEffect(() => {
    configAPI.getConfig()
      .then(r => {
        const byKey = Object.fromEntries((r.data || []).map(c => [c.key, c.value]));
        if (byKey.exchange_rate) {
          const cfgRate = parseFloat(byKey.exchange_rate) || 46;
          setDefaultRate(cfgRate);
          setForm(f => ({ ...f, exchange_rate: f.exchange_rate || byKey.exchange_rate }));
        }
        if (byKey.kg_delivery_price) setKgPrice(parseFloat(byKey.kg_delivery_price) || 18);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await cheeseAPI.updateTransit(item.id, {
        status:                  'received',
        name:                    form.name || null,
        photo_url:               form.photo_url || null,
        catalog_sku:             form.catalog_sku || null,
        received_at:             form.received_at || today,
        supplier_article:        form.supplier_article || null,
        item_id:                 form.item_id ? Number(form.item_id) : null,
        sizes_qty:               form.sizes_qty,
        has_box:                 form.box_type === 'original',
        box_type:                form.box_type || 'brand',
        purchase_price_per_pair: form.purchase_price_per_pair ? parseFloat(form.purchase_price_per_pair) : null,
        materials:               form.materials || null,
        manufacturer:            form.manufacturer || null,
        weight_kg:               form.weight_kg ? parseFloat(form.weight_kg) : null,
        boxes_delivery_uah:      form.boxes_delivery_uah !== '' ? parseFloat(form.boxes_delivery_uah) : null,
        order_date:              form.order_date || null,
        china_ship_date:         form.china_ship_date || null,
        category_name:           form.category_name || null,
        category_id_val:         form.category_id_val ? Number(form.category_id_val) : null,
        exchange_rate:           form.exchange_rate ? parseFloat(form.exchange_rate) : null,
        market_price_avg:        form.market_price_avg ? parseFloat(form.market_price_avg) : null,
        code:                    form.code || null,
        sizes_range:             form.sizes_range || null,
        batch_id:                form.batch_id ? Number(form.batch_id) : null,
      });
      if (category && form.supplier_article &&
          articleNumSuffix(form.supplier_article) > articleNumSuffix(category.last_article)) {
        await cheeseAPI.updateCategory(category.id, { last_article: form.supplier_article }).catch(() => {});
      }
      onSave(res.data);
      onClose();
    } catch (e) {
      alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const totalPrs = totalPairs(form.sizes_qty);

  // Подсветка пустых полей
  const e_ = (v) => !v && v !== 0;
  const ef = (v) => e_(v) ? ' mng-field--empty' : '';

  return (
    <div className="mng-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mng-modal rcv-modal">
        <div className="mng-modal-header">
          <h3 className="mng-modal-title">Оформление получения</h3>
          <button className="mng-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="rcv-modal-body">

          {/* ── Левая панель — форма ── */}
          <div className="rcv-form-panel">

            <div className="rcv-section-title">Основное</div>

            <div className={`mng-field${ef(form.name)}`}>
              <label className="mng-label">Название товара</label>
              <input className="mng-input" value={form.name}
                onChange={e => set('name', e.target.value)} />
            </div>

            <div className="mng-field-row">
              <div className={`mng-field${ef(form.catalog_sku)}`}>
                <label className="mng-label">Артикул каталога</label>
                <input className="mng-input" value={form.catalog_sku}
                  onChange={e => set('catalog_sku', e.target.value)} placeholder="SKU…" />
              </div>
              <div className={`mng-field${ef(form.photo_url)}`}>
                <label className="mng-label">Фото URL</label>
                <input className="mng-input" value={form.photo_url}
                  onChange={e => set('photo_url', e.target.value)} placeholder="https://…" />
              </div>
            </div>

            <div className="mng-field">
              <label className="mng-label">Коробка / упаковка</label>
              <select className="mng-input" value={form.box_type || 'brand'}
                onChange={e => { const bt = e.target.value; setForm(f => ({ ...f, box_type: bt, has_box: bt === 'original' })); }}>
                <option value="original">Оригінальна коробка 📦</option>
                <option value="brand">Брендова коробка</option>
                <option value="white">Біла коробка</option>
              </select>
            </div>

            <div className="rcv-section-title">Идентификаторы</div>

            <div className={`mng-field${ef(form.supplier_article)}`}>
              <label className="mng-label">Артикул поставщика</label>
              <input className="mng-input" value={form.supplier_article}
                onChange={e => set('supplier_article', e.target.value)}
                placeholder="напр. 10551-043" />
              {category?.last_article && (
                <span className="rcv-hint">Предыдущий: {category.last_article}</span>
              )}
            </div>

            <div className="mng-field-row">
              <div className={`mng-field${ef(form.item_id)}`}>
                <label className="mng-label">Item ID</label>
                <input className="mng-input" type="text" inputMode="decimal" value={form.item_id}
                  onChange={e => set('item_id', e.target.value)} />
              </div>
              <div className={`mng-field${ef(form.received_at)}`}>
                <label className="mng-label">Дата получения</label>
                <input className="mng-input" type="date" value={form.received_at}
                  onChange={e => set('received_at', e.target.value)} />
              </div>
              {batches.length > 0 && (
                <div className="mng-field">
                  <label className="mng-label">Партия</label>
                  <select className="mng-input" value={form.batch_id}
                    onChange={e => set('batch_id', e.target.value)}>
                    <option value="">— без партии —</option>
                    {batches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}{b.received_date ? ` (${b.received_date})` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="rcv-section-title">Категория</div>

            <div className="mng-field-row">
              <div className={`mng-field${ef(form.category_name)}`} style={{ flex: 2 }}>
                <label className="mng-label">Название категории</label>
                <input className="mng-input" value={form.category_name}
                  onChange={e => set('category_name', e.target.value)}
                  placeholder={category?.name || '—'} />
              </div>
              <div className="mng-field">
                <label className="mng-label">Код</label>
                <input className="mng-input" value={form.category_id_val}
                  readOnly style={{ opacity: 0.7, cursor: 'default' }} />
              </div>
            </div>

            <div className="rcv-section-title">Цена и логистика</div>

            <div className="mng-field-row">
              <div className={`mng-field${ef(form.purchase_price_per_pair)}`}>
                <label className="mng-label">Опт / пара, $</label>
                <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.01"
                  value={form.purchase_price_per_pair}
                  onChange={e => set('purchase_price_per_pair', e.target.value.replace(',', '.'))}
                  placeholder="0.00" />
              </div>
              <div className={`mng-field${ef(form.exchange_rate)}`}>
                <label className="mng-label">Курс $ → ₴</label>
                <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.5"
                  value={form.exchange_rate}
                  onChange={e => set('exchange_rate', e.target.value.replace(',', '.'))}
                  placeholder="46" />
              </div>
              <div className="mng-field">
                <label className="mng-label">Закуп ₴ / пара</label>
                {(() => {
                  const uah = parseFloat(form.purchase_price_per_pair) * parseFloat(form.exchange_rate);
                  return (
                    <input className="mng-input" readOnly
                      value={(!isNaN(uah) && uah > 0) ? Math.round(uah).toLocaleString('ru-RU') : ''}
                      placeholder="авто"
                      style={{ opacity: 0.75, cursor: 'default', background: 'rgba(74,144,226,0.06)' }} />
                  );
                })()}
              </div>
            </div>
            <div className="mng-field-row">
              <div className={`mng-field${ef(form.weight_kg)}`}>
                <label className="mng-label">Вес, кг</label>
                <input className="mng-input" type="text" inputMode="decimal" min="0" step="0.01"
                  value={form.weight_kg}
                  onChange={e => set('weight_kg', e.target.value.replace(',', '.'))}
                  placeholder="0.0" />
              </div>
              <div className="mng-field">
                <label className="mng-label">Коробки + дост, ₴</label>
                <input className="mng-input" type="text" inputMode="decimal" min="0" step="1"
                  value={form.boxes_delivery_uah}
                  onChange={e => set('boxes_delivery_uah', e.target.value.replace(',', '.'))}
                  placeholder="0" />
              </div>
            </div>
            <div className="mng-field-row">
              <div className="mng-field">
                <label className="mng-label">Країна виробництва</label>
                <select className="mng-input" value={form.manufacturer || "В'єтнам"}
                  onChange={e => set('manufacturer', e.target.value)}>
                  <option value="В'єтнам">В'єтнам</option>
                  <option value="Китай">Китай</option>
                </select>
              </div>
              <div className="mng-field">
                <label className="mng-label">Дост / пара, $</label>
                {(() => {
                  const wKg = parseFloat(form.weight_kg) || 0;
                  const pairs = totalPairs(form.sizes_qty);
                  const del = pairs > 0 && wKg > 0
                    ? ((wKg * kgPrice) + (wKg * 20) / 100 * 3) / pairs
                    : null;
                  return (
                    <input className="mng-input" readOnly
                      value={del != null ? del.toFixed(2) : ''}
                      placeholder="авто"
                      style={{ opacity: 0.75, cursor: 'default', background: 'rgba(74,144,226,0.06)' }} />
                  );
                })()}
              </div>
            </div>

            <div className={`mng-field${ef(form.materials)}`}>
              <label className="mng-label">Материалы</label>
              <MaterialsPicker value={form.materials} onChange={v => set('materials', v)} />
            </div>

            <div className="mng-field-row">
              <div className={`mng-field${ef(form.order_date)}`}>
                <label className="mng-label">Дата заказа</label>
                <input className="mng-input" type="date" value={form.order_date}
                  onChange={e => set('order_date', e.target.value)} />
              </div>
              <div className={`mng-field${ef(form.china_ship_date)}`}>
                <label className="mng-label">Отправка из Китая</label>
                <input className="mng-input" type="date" value={form.china_ship_date}
                  onChange={e => set('china_ship_date', e.target.value)} />
              </div>
            </div>

            {/* Опт+дост и Маржа — расчётные поля */}
            {(() => {
              const wKg    = parseFloat(form.weight_kg) || 0;
              const pairs  = totalPairs(form.sizes_qty);
              const opt$   = parseFloat(form.purchase_price_per_pair) || 0;
              const rate   = parseFloat(form.exchange_rate) || defaultRate;
              const market = parseFloat(form.market_price_avg) || 0;
              const boxUah = parseFloat(form.boxes_delivery_uah) || 0;
              const del$   = pairs > 0 && wKg > 0 ? ((wKg * kgPrice) + (wKg * 20) / 100 * 3) / pairs : 0;
              const raw    = del$ * rate + opt$ * rate + boxUah;
              const optDel = raw > 0 ? Math.ceil((raw + 100) / 10) * 10 : 0;
              const margin = market > 0 && optDel > 0 ? market - optDel : null;
              return (
                <div className="mng-field-row">
                  <div className="mng-field">
                    <label className="mng-label">Опт+дост, ₴</label>
                    <input className="mng-input" readOnly
                      value={optDel > 0 ? optDel.toLocaleString('ru-RU') : ''}
                      placeholder="авто"
                      style={{ opacity: 0.8, cursor: 'default', background: 'rgba(74,144,226,0.06)' }} />
                  </div>
                  <div className="mng-field">
                    <label className="mng-label">Маржа, ₴</label>
                    <input className="mng-input" readOnly
                      value={margin != null ? Math.round(margin).toLocaleString('ru-RU') : ''}
                      placeholder="авто"
                      style={{
                        opacity: 0.8, cursor: 'default',
                        background: margin != null ? (margin > 0 ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)') : 'rgba(74,144,226,0.06)',
                        color: margin != null ? (margin > 0 ? '#4ade80' : '#f87171') : undefined,
                        fontWeight: margin != null ? 600 : undefined,
                      }} />
                  </div>
                </div>
              );
            })()}

            <div className="rcv-section-title">Публикация в Telegram</div>

            <div className="mng-field-row">
              <div className={`mng-field${ef(form.market_price_avg)}`}>
                <label className="mng-label">Цена дроп, ₴</label>
                <input className="mng-input" type="text" inputMode="decimal" min="0" step="1"
                  value={form.market_price_avg}
                  onChange={e => set('market_price_avg', e.target.value.replace(',', '.'))}
                  placeholder="напр. 1250" />
              </div>
              <div className={`mng-field${ef(form.code)}`}>
                <label className="mng-label">Код товара</label>
                <input className="mng-input" value={form.code}
                  onChange={e => set('code', e.target.value)}
                  placeholder="напр. 10551-043" />
              </div>
              <div className="mng-field">
                <label className="mng-label">Диапазон размеров</label>
                <input className="mng-input" value={form.sizes_range}
                  readOnly style={{ opacity: 0.75, cursor: 'default', background: 'rgba(74,144,226,0.06)' }}
                  placeholder="авто из размеров" />
              </div>
            </div>

            <div className={`rcv-section-title${totalPrs === 0 ? ' rcv-section-title--empty' : ''}`}>
              Размеры и количество{totalPrs > 0 && <span className="rcv-section-count">({totalPrs} пар)</span>}
            </div>

            <SizesEditor value={form.sizes_qty} onChange={v => {
              setForm(f => ({ ...f, sizes_qty: v, sizes_range: computeSizesRange(v) }));
            }} />

          </div>

          {/* ── Правая панель — поставщики ── */}
          <div className="rcv-sup-panel">
            <div className="rcv-sup-title">Наличие у поставщиков</div>
            <SupplierPanel catalogSku={form.catalog_sku || item?.catalog_sku} />
          </div>

        </div>
        <div className="mng-modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Отмена</button>
          <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Сохраняем…' : '✓ Получено'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Вкладка В пути / Получено / Опубликовано ───────────────────────────────
function TransitSubtab({ search = '', statusFixed = null }) {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(false);
  const [statusFilter, setStatusFilter] = useState(statusFixed || 'transit');
  const [modal, setModal]           = useState(null);
  const [tgModal, setTgModal]       = useState(null);
  const [receiveModal, setReceiveModal] = useState(null);
  const [rate, setRate]             = useState(46);
  const [kgPrice, setKgPrice]       = useState(18);
  const [batches, setBatches]       = useState([]);
  const [zoomSrc, setZoomSrc]       = useState(null);

  const activeStatus = statusFixed || statusFilter;

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [tr, cfg, batchRes] = await Promise.all([
        cheeseAPI.listTransit(activeStatus || undefined),
        configAPI.getConfig().catch(() => ({ data: [] })),
        cheeseAPI.listBatches().catch(() => ({ data: [] })),
      ]);
      setBatches(batchRes.data || []);
      setItems(tr.data);
      if (cfg.data) {
        const byKey = Object.fromEntries(cfg.data.map(c => [c.key, c.value]));
        if (byKey.exchange_rate)   setRate(parseFloat(byKey.exchange_rate) || 46);
        if (byKey.kg_delivery_price) setKgPrice(parseFloat(byKey.kg_delivery_price) || 18);
      }
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, statusFixed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (saved) => setItems(prev => {
    const idx = prev.findIndex(o => o.id === saved.id);
    if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n; }
    return [saved, ...prev];
  });

  const handlePatch = async (id, fields) => {
    try {
      const res = await cheeseAPI.updateTransit(id, fields);
      handleSave(res.data);
    } catch { /* silent */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить товар?')) return;
    await cheeseAPI.deleteTransit(id);
    setItems(prev => prev.filter(o => o.id !== id));
  };

  const handleTgDone = async () => {
    if (tgModal?.id) {
      await cheeseAPI.updateTransit(tgModal.id, { status: 'published' }).catch(() => {});
    }
    const res = await cheeseAPI.listTransit(activeStatus || undefined);
    setItems(res.data);
    setTgModal(null);
  };

  const handleExport = async () => {
    const labels = { transit: 'В пути', received: 'Получено', published: 'Опубликовано' };
    const label = labels[activeStatus] || 'все товары';
    if (!window.confirm(`Скачать список "${label}" в XLSX?`)) return;
    try {
      const res = await cheeseAPI.exportTransitXlsx(activeStatus || undefined);
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement('a'), { href: url, download: 'cheese_transit.xlsx' }).click();
      URL.revokeObjectURL(url);
    } catch { alert('Ошибка экспорта'); }
  };

  const nextStatusMap = { transit: 'received', received: 'published' };
  const nextStatusLabel = { transit: 'Получено', received: 'Опубликовано' };

  const handleNextStatus = (id) => {
    const next = nextStatusMap[activeStatus];
    if (!next) return;
    if (next === 'received') {
      const item = items.find(it => it.id === id);
      if (item) { setReceiveModal(item); return; }
    }
    cheeseAPI.updateTransit(id, { status: next })
      .then(() => setItems(prev => prev.filter(it => it.id !== id)))
      .catch(() => alert('Ошибка смены статуса'));
  };

  // Фиксированный размерный ряд 35–47
  const allSizes = ['35','36','37','38','39','40','41','42','43','44','45','46','47'];

  const q = search.trim().toLowerCase();
  const filteredItems = q
    ? items.filter(it =>
        (it.name || '').toLowerCase().includes(q) ||
        (it.catalog_sku || '').toLowerCase().includes(q) ||
        (it.supplier_article || '').toLowerCase().includes(q)
      )
    : items;

  const arrivalTs = (it) => {
    if (!it.china_ship_date) return Infinity;
    const d = new Date(it.china_ship_date);
    d.setDate(d.getDate() + 30);
    return d.getTime();
  };
  const visibleItems = activeStatus === 'transit'
    ? [...filteredItems].sort((a, b) => arrivalTs(a) - arrivalTs(b))
    : filteredItems;

  const filters = [['', 'Все'], ['transit', 'В пути'], ['received', 'Получено'], ['published', 'Опубликовано']];

  return (
    <div className="cheese-subtab">
      {loadError && <div className="cheese-load-error">Ошибка загрузки. <button className="btn btn-sm btn-secondary" onClick={load}>Повторить</button></div>}
      <div className="cheese-toolbar">
        <button className="btn btn-primary btn-sm" onClick={() => setModal('create')}>
          + Добавить товар
        </button>
        <button className="btn btn-secondary btn-sm" onClick={handleExport}>
          Скачать XLS
        </button>
        {!statusFixed && (
          <div className="cheese-filter-group">
            {filters.map(([v, label]) => (
              <button key={v}
                className={`cheese-filter-btn${statusFilter === v ? ' cheese-filter-btn--active' : ''}`}
                onClick={() => setStatusFilter(v)}>
                {label}
              </button>
            ))}
          </div>
        )}
        {statusFixed && nextStatusMap[statusFixed] && (
          <span className="cheese-toolbar-hint">
            Нажмите ✓ / ✈ в строке чтобы переместить в «{nextStatusLabel[statusFixed]}»
          </span>
        )}
        <div className="cheese-rate-controls">
          <label className="cheese-rate-label">
            Курс $:
            <input type="text" inputMode="decimal" className="cheese-rate-input" value={rate}
              onChange={e => setRate(parseFloat(e.target.value.replace(',', '.')) || 46)} min="1" step="0.5" />
            ₴
          </label>
          <label className="cheese-rate-label">
            Дост. за кг:
            <input type="text" inputMode="decimal" className="cheese-rate-input" value={kgPrice}
              onChange={e => setKgPrice(parseFloat(e.target.value.replace(',', '.')) || 18)} min="1" step="0.5" />
            $
          </label>
        </div>
        <div className="cheese-toolbar-stats">
          <span>{visibleItems.length}{q ? `/${items.length}` : ''} товаров</span>
        </div>
      </div>

      {loading && <div className="cheese-empty">Загрузка…</div>}

      {!loading && items.length === 0 && (
        <div className="cheese-empty">
          <div className="cheese-empty-icon">📦</div>
          <div>Нет товаров</div>
        </div>
      )}
      {!loading && items.length > 0 && visibleItems.length === 0 && (
        <div className="cheese-empty">Ничего не найдено по запросу «{search}»</div>
      )}
      {!loading && visibleItems.length > 0 && (
        <div className="cheese-transit-table-wrap">
          <table className="cheese-transit-table" data-cb="0">
            <thead>
              <tr>
                <th className="cheese-th-photo cg-id cg-sticky-1">Фото</th>
                <th className="cheese-th-name cg-id cg-sticky-2">Название</th>
                <th className="cheese-th-date cg-id cg-sticky-3">±Прибытие</th>
                <th className="cheese-th-sku cg-id cg-sticky-4">Арт</th>
                <th className="cheese-th-num cg-price">Опт $</th>
                <th className="cheese-th-num cg-price">Опт ₴</th>
                <th className="cheese-th-num cg-log">Вес кг</th>
                <th className="cheese-th-num cg-log">Дост $</th>
                <th className="cheese-th-num cg-log">Кор+дост ₴</th>
                <th className="cheese-th-num cg-log">Опт+дост ₴</th>
                <th className="cheese-th-num cg-market">Дроп ₴</th>
                <th className="cheese-th-num cg-market">Маржа ₴</th>
                <th className="cheese-th-num cg-market">Себест $</th>
                {allSizes.map(s => <th key={s} className="cheese-th-size cg-size">{s}</th>)}
                <th className="cheese-th-num cg-total">Всего</th>
                <th className="cheese-th-num cg-total">Закупка ₴</th>
                <th className="cheese-th-num cg-total">Закупка $</th>
                <th className="cheese-th-num cg-total">Без дост $</th>
                <th className="cheese-th-num cg-total">Дост итого $</th>
                <th className="cheese-th-date cg-dates">Заказ</th>
                <th className="cheese-th-date cg-dates">Отпр Китай</th>
                <th className="cheese-th-actions">Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map(item => (
                <TransitRow key={item.id} item={item} allSizes={allSizes}
                  rate={rate} kgPrice={kgPrice}
                  onEdit={setModal} onTg={activeStatus !== 'transit' ? setTgModal : null}
                  onDelete={handleDelete} onPatch={handlePatch}
                  onNextStatus={nextStatusMap[activeStatus] ? handleNextStatus : null}
                  onZoom={setZoomSrc} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <TransitModal
          item={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {tgModal && (
        <TelegramModal item={tgModal} onClose={() => setTgModal(null)} onDone={handleTgDone} />
      )}
      {receiveModal && (
        <ReceiveModal
          item={receiveModal}
          batches={batches}
          onClose={() => setReceiveModal(null)}
          onSave={saved => {
            setItems(prev => prev.filter(it => it.id !== saved.id));
            setReceiveModal(null);
          }}
        />
      )}
      {zoomSrc && (
        <div className="bnote-lightbox" onClick={() => setZoomSrc(null)}>
          <img src={proxyImageUrl(zoomSrc)} alt="" className="bnote-lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="bnote-lightbox-close" onClick={() => setZoomSrc(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── Модальное окно партии ──────────────────────────────────────────────────
// ── Блок заметки (фото + текст) ───────────────────────────────────────────
function fixUploadUrl(url) {
  if (!url) return url;
  if (url.includes('/uploads/') && !url.includes('/api/v1/uploads/')) {
    return url.replace('/uploads/', '/api/v1/uploads/');
  }
  return url;
}

function NoteBlock({ block, onChange, onDelete, apiBase, onZoom, onDragStart, onDragOver, onDragEnd, isDragOver }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const res = await cheeseAPI.uploadBatchPhoto(file);
      onChange({ ...block, photo_url: res.data.url });
    } catch { /* silent */ }
    finally { setUploading(false); }
  }, [block, onChange, apiBase]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  return (
    <div
      className={`bnote-block${isDragOver ? ' bnote-block--dragover' : ''}`}
      onDragOver={e => { e.preventDefault(); onDragOver?.(block.id); }}
      onDrop={e => { if (!e.dataTransfer.files.length) { e.preventDefault(); onDragEnd?.(); } }}
    >
      <div
        className="bnote-drag-handle"
        draggable
        onDragStart={e => { e.stopPropagation(); onDragStart?.(block.id); }}
        onDragEnd={onDragEnd}
        title="Перетащить"
      >⠿</div>
      <button className="bnote-block-del" onClick={onDelete} title="Удалить блок">✕</button>
      <div className="bnote-block-body">
        {/* Зона фото */}
        <div
          className={`bnote-photo-zone${dragging ? ' bnote-photo-zone--drag' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => { if (!block.photo_url) document.getElementById(`bnote-file-${block.id}`)?.click(); }}
        >
          <input
            id={`bnote-file-${block.id}`}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
          />
          {uploading ? (
            <span className="bnote-upload-hint">Загрузка…</span>
          ) : block.photo_url ? (
            <>
              <img
                src={fixUploadUrl(block.photo_url)}
                alt=""
                className="bnote-photo-img"
                onClick={e => { e.stopPropagation(); onZoom(fixUploadUrl(block.photo_url)); }}
                onError={e => { e.target.style.display = 'none'; }}
              />
              <button
                className="bnote-photo-clear"
                title="Удалить фото"
                onClick={e => { e.stopPropagation(); onChange({ ...block, photo_url: '' }); }}
              >✕</button>
            </>
          ) : (
            <span className="bnote-upload-hint">
              {dragging ? 'Отпустите фото' : '+ фото'}
            </span>
          )}
        </div>
        {/* Текст */}
        <textarea
          className="bnote-text"
          placeholder="Текст заметки…"
          value={block.text}
          onChange={e => onChange({ ...block, text: e.target.value })}
        />
      </div>
    </div>
  );
}

function BatchModal({ batch, onSave, onClose }) {
  const today = new Date().toISOString().slice(0, 10);

  const parseRichNotes = (raw) => {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  };

  const [name, setName] = useState(batch?.name || '');
  const [receivedDate, setReceivedDate] = useState(batch?.received_date || today);
  const [blocks, setBlocks] = useState(() => {
    const parsed = parseRichNotes(batch?.rich_notes);
    return parsed.length ? parsed : [{ id: Date.now().toString(), photo_url: '', text: '' }];
  });
  const [saving, setSaving] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const dragSrcId = useRef(null);

  const apiBase = window.location.protocol + '//' + window.location.host;

  const [zoomSrc, setZoomSrc] = useState(null);

  const handleBlockDragStart = (id) => { dragSrcId.current = id; };
  const handleBlockDragOver = (id) => {
    if (!dragSrcId.current || dragSrcId.current === id) return;
    setDragOverId(id);
    setBlocks(prev => {
      const from = prev.findIndex(b => b.id === dragSrcId.current);
      const to   = prev.findIndex(b => b.id === id);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      dragSrcId.current = id;
      return next;
    });
  };
  const handleBlockDragEnd = () => { dragSrcId.current = null; setDragOverId(null); };

  const addBlock = () => setBlocks(b => [...b, { id: Date.now().toString(), photo_url: '', text: '' }]);
  const delBlock = (id) => setBlocks(b => b.filter(bl => bl.id !== id));
  const updateBlock = (updated) => setBlocks(b => b.map(bl => bl.id === updated.id ? updated : bl));

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const richNotes = JSON.stringify(blocks.filter(b => b.photo_url || b.text.trim()));
    try {
      const payload = { name, received_date: receivedDate, rich_notes: richNotes };
      let res;
      if (batch?.id) {
        res = await cheeseAPI.updateBatch(batch.id, payload);
      } else {
        res = await cheeseAPI.createBatch(payload);
      }
      onSave(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mng-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mng-modal bnote-modal">
        <div className="mng-modal-header">
          <h3 className="mng-modal-title">{batch?.id ? 'Заметки партии' : 'Создать партию'}</h3>
          <button className="mng-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="bnote-modal-top">
          <div className="mng-field" style={{ flex: 1, margin: 0 }}>
            <label className="mng-label">Название</label>
            <input className="mng-input" value={name} onChange={e => setName(e.target.value)} placeholder="Партия Nike январь 2025" autoFocus />
          </div>
          <div className="mng-field" style={{ width: 160, margin: 0 }}>
            <label className="mng-label">Дата получения</label>
            <input className="mng-input" type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)} />
          </div>
        </div>
        <div className="bnote-modal-body">
          {blocks.map(bl => (
            <NoteBlock
              key={bl.id}
              block={bl}
              onChange={updateBlock}
              onDelete={() => delBlock(bl.id)}
              apiBase={apiBase}
              onZoom={setZoomSrc}
              onDragStart={handleBlockDragStart}
              onDragOver={handleBlockDragOver}
              onDragEnd={handleBlockDragEnd}
              isDragOver={dragOverId === bl.id}
            />
          ))}
          <button className="bnote-add-block" onClick={addBlock}>+ Добавить блок</button>
        </div>
        <div className="mng-modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Отмена</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Сохранение…' : batch?.id ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </div>

      {zoomSrc && (
        <div className="bnote-lightbox" onClick={() => setZoomSrc(null)}>
          <img src={zoomSrc} alt="" className="bnote-lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="bnote-lightbox-close" onClick={() => setZoomSrc(null)}>✕</button>
        </div>
      )}
    </div>
  );
}


// ── Таблица товаров с опциональными чекбоксами ────────────────────────────
function TransitTableSection({ items, allSizes, rate, kgPrice, selectedIds, onToggle, onToggleAll, onEdit, onTg, onDelete, onPatch, onNextStatus, onEasydrop }) {
  const [zoomSrc, setZoomSrc] = useState(null);
  if (items.length === 0) return <div className="cheese-empty" style={{ padding: '12px 0' }}>Нет товаров</div>;
  const hasCheckboxes = !!onToggle;
  const allSelected = hasCheckboxes && items.length > 0 && items.every(it => selectedIds?.has(it.id));

  return (
    <>
    <div className="cheese-transit-table-wrap">
      <table className="cheese-transit-table" data-cb={hasCheckboxes ? '1' : '0'}>
        <thead>
          <tr>
            {hasCheckboxes && <th className="cheese-th-check"><input type="checkbox" checked={allSelected} onChange={() => onToggleAll?.(items.map(it => it.id))} /></th>}
            <th className="cheese-th-photo cg-id cg-sticky-1">Фото</th>
            <th className="cheese-th-name cg-id cg-sticky-2">Название</th>
            <th className="cheese-th-date cg-id cg-sticky-3">±Прибытие</th>
            <th className="cheese-th-sku cg-id cg-sticky-4">Арт</th>
            <th className="cheese-th-num cg-price">Опт $</th>
            <th className="cheese-th-num cg-price">Опт ₴</th>
            <th className="cheese-th-num cg-log">Вес кг</th>
            <th className="cheese-th-num cg-log">Дост $</th>
            <th className="cheese-th-num cg-log">Кор+дост ₴</th>
            <th className="cheese-th-num cg-log">Опт+дост ₴</th>
            <th className="cheese-th-num cg-market">Дроп ₴</th>
            <th className="cheese-th-num cg-market">Маржа ₴</th>
            <th className="cheese-th-num cg-market">Себест $</th>
            {allSizes.map(s => <th key={s} className="cheese-th-size cg-size">{s}</th>)}
            <th className="cheese-th-num cg-total">Всего</th>
            <th className="cheese-th-num cg-total">Закупка ₴</th>
            <th className="cheese-th-num cg-total">Закупка $</th>
            <th className="cheese-th-num cg-total">Без дост $</th>
            <th className="cheese-th-num cg-total">Дост итого $</th>
            <th className="cheese-th-date cg-dates">Заказ</th>
            <th className="cheese-th-date cg-dates">Отпр Китай</th>
            <th className="cheese-th-actions">Действия</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <TransitRow key={item.id} item={item} allSizes={allSizes} rate={rate} kgPrice={kgPrice}
              onEdit={onEdit} onTg={onTg} onDelete={onDelete} onPatch={onPatch}
              onNextStatus={onNextStatus} onEasydrop={onEasydrop}
              checked={hasCheckboxes ? !!selectedIds?.has(item.id) : undefined}
              onCheck={hasCheckboxes ? onToggle : undefined}
              onZoom={setZoomSrc} />
          ))}
        </tbody>
      </table>
    </div>
    {zoomSrc && (
      <div className="bnote-lightbox" onClick={() => setZoomSrc(null)}>
        <img src={proxyImageUrl(zoomSrc)} alt="" className="bnote-lightbox-img" onClick={e => e.stopPropagation()} />
        <button className="bnote-lightbox-close" onClick={() => setZoomSrc(null)}>✕</button>
      </div>
    )}
    </>
  );
}


// ── Модалка публікації/прив'язки товару до EasyDrop ────────────────────────
// Спочатку показує автоматичний збіг за артикулом (якщо знайшовся) — щоб
// не наплодити дублікатів для товару, що вже є в EasyDrop. Є і ручний пошук,
// і кнопка "все одно створити новий", якщо це справді інший товар.
function EasydropLinkModal({ item, autoMatch, onClose, onLinked, onCreateNew }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const doSearch = (q) => {
    setQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await cheeseAPI.easydropSearch(q.trim());
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
  };

  const doLink = async (edItem) => {
    setBusy(true);
    try {
      await cheeseAPI.easydropLink(item.id, edItem.id);
      onLinked(edItem.id);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка прив\'язки');
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cheese-modal" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
        <div className="cheese-modal-header">
          <div>
            <h3 className="cheese-modal-title">Товар в EasyDrop</h3>
            <div className="cheese-modal-subtitle">{item.name}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <CheesePhoto url={item.tg_photo_url || item.photo_url} size={140} />
            <div style={{ fontSize: 13, opacity: 0.7 }}>Наша картка — звіряйте фото з тим, що знайдеться нижче</div>
          </div>

          {autoMatch && (
            <div style={{ padding: 14, border: '1px solid var(--lg-border)', borderRadius: 8 }}>
              <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 10 }}>Знайдено за збігом артикулу:</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <CheesePhoto url={autoMatch.photo_url} size={110} />
                  <span style={{ fontSize: 15 }}>{autoMatch.model} <span style={{ opacity: 0.6 }}>({autoMatch.code}, id {autoMatch.id})</span></span>
                </div>
                <button className="btn btn-primary" disabled={busy} onClick={() => doLink(autoMatch)}>Прив'язати</button>
              </div>
            </div>
          )}

          <div>
            <label className="mng-label" style={{ marginBottom: 4, display: 'block' }}>
              {autoMatch ? 'Або пошук вручну' : 'Автоматично не знайдено — пошук вручну'} (артикул/назва)
            </label>
            <input className="mng-input" value={query} onChange={e => doSearch(e.target.value)} placeholder="Наприклад: AD125" autoFocus={!autoMatch} />
          </div>
          {searching && <div style={{ fontSize: 13, opacity: 0.6 }}>Пошук…</div>}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <div style={{ fontSize: 13, opacity: 0.6 }}>Нічого не знайдено</div>
          )}
          {!searching && results.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
              {results.map(r => (
                <button key={r.id} type="button" disabled={busy} onClick={() => doLink(r)}
                  style={{ textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                            padding: '8px 12px', fontSize: 13, background: 'none', border: '1px solid var(--lg-border)',
                            borderRadius: 8, cursor: 'pointer', color: 'var(--lg-text)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CheesePhoto url={r.photo_url} size={72} />
                    {r.model} <span style={{ opacity: 0.6 }}>({r.code})</span>
                  </span>
                  <span style={{ opacity: 0.6 }}>id {r.id}</span>
                </button>
              ))}
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--lg-border)', paddingTop: 12 }}>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onCreateNew}>
              + Це інший товар — створити НОВИЙ в EasyDrop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


const EASYDROP_FIELD_LABELS = {
  model: 'Назва', brand: 'Категорія (brand)', code: 'Артикул', manufacturer: 'Виробник',
  material: 'Матеріал', link: 'Посилання', drop_price: 'Дроп ціна',
};

// ── Модалка попереднього перегляду перед записом в EasyDrop ────────────────
// Нічого не пише сама — спочатку тягне read-only preview (що саме буде
// відправлено/зміниться), і тільки після явного підтвердження викликає
// реальний create/restock.
const EASYDROP_ACTION_LABELS = { create: 'Створено', link: "Прив'язано", unlink: "Відв'язано", restock: 'Оновлено' };

function EasydropPreviewModal({ mode, item, onClose, onDone, onUnlink }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);

  const toggleHistory = async () => {
    if (historyOpen) { setHistoryOpen(false); return; }
    setHistoryOpen(true);
    if (history === null) {
      try {
        const res = await cheeseAPI.easydropHistory(item.id);
        setHistory(res.data || []);
      } catch { setHistory([]); }
    }
  };

  const [editedFields, setEditedFields] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = mode === 'create'
          ? await cheeseAPI.easydropPreviewCreate(item.id)
          : await cheeseAPI.easydropPreviewRestock(item.id);
        if (!cancelled) {
          setData(res.data);
          if (mode === 'create') setEditedFields(res.data.fields);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Помилка завантаження перегляду');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, item.id]);

  const handleConfirm = async () => {
    // Окреме останнє підтвердження — попередній перегляд це не сам запис,
    // тут остання можливість зупинитись перед реальною зміною в EasyDrop.
    const question = mode === 'create'
      ? 'Точно створити цей товар в EasyDrop? Це реальний запис.'
      : 'Точно записати ці зміни (розміри/ціна) в EasyDrop? Це реальний запис, скасувати не можна.';
    if (!window.confirm(question)) return;

    setConfirming(true);
    try {
      const res = mode === 'create'
        ? await cheeseAPI.easydropCreate(item.id, editedFields)
        : await cheeseAPI.easydropRestock(item.id);
      onDone(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка запису в EasyDrop');
      setConfirming(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cheese-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="cheese-modal-header">
          <div>
            <h3 className="cheese-modal-title">{mode === 'create' ? 'Створити новий товар в EasyDrop' : 'Оновити розміри в EasyDrop'}</h3>
            <div className="cheese-modal-subtitle">{item.name}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading && <div style={{ fontSize: 13, opacity: 0.6 }}>Завантаження перегляду…</div>}
          {error && <div style={{ fontSize: 13, color: '#f87171' }}>{error}</div>}

          {data && mode === 'create' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <CheesePhoto url={data.photo_url} size={90} />
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {data.photo_url
                    ? "Це фото буде обрізано по центру під квадрат і завантажено файлом (не посиланням) при створенні товару"
                    : "У картки немає фото — товар створиться без нього"}
                </div>
              </div>
              <div>
                <div className="mng-label" style={{ marginBottom: 6 }}>Поля, що будуть відправлені (можна поправити):</div>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <tbody>
                    {Object.keys(data.fields).map(k => (
                      <tr key={k}>
                        <td style={{ padding: '4px 8px', opacity: 0.7, whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{EASYDROP_FIELD_LABELS[k] || k}</td>
                        <td style={{ padding: '4px 8px' }}>
                          <input
                            className="mng-input"
                            style={{ fontSize: 13, padding: '4px 8px' }}
                            type={k === 'drop_price' ? 'number' : 'text'}
                            value={editedFields[k] ?? ''}
                            onChange={e => setEditedFields(f => ({ ...f, [k]: k === 'drop_price' ? Number(e.target.value) : e.target.value }))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="mng-label" style={{ marginBottom: 6 }}>Розміри, що будуть виставлені:</div>
                {data.sizes.length === 0 ? (
                  <div style={{ fontSize: 13, opacity: 0.6 }}>Немає розмірів у картці — товар створиться зі стандартною сіткою по 0</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.sizes.map(s => (
                      <span key={s.val} style={{ padding: '4px 10px', border: '1px solid var(--lg-border)', borderRadius: 6, fontSize: 13 }}>
                        {s.val}: <strong>{s.qty}</strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {data && mode === 'restock' && (
            <>
            {data.price && (
              <div style={{ padding: 10, border: '1px solid var(--lg-border)', borderRadius: 8,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, opacity: 0.7 }}>Дроп ціна</span>
                {data.price.will_change ? (
                  <span style={{ fontSize: 13 }}>
                    {data.price.current} → <strong style={{ color: 'var(--lg-accent)' }}>{data.price.new}</strong>
                  </span>
                ) : (
                  <span style={{ fontSize: 13, opacity: 0.6 }}>{data.price.current ?? '—'} (без змін)</span>
                )}
              </div>
            )}
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                {(() => {
                  const totalBefore = data.rows.reduce((s, r) => s + (r.matched ? r.current_qty : 0), 0);
                  const totalDelta  = data.rows.reduce((s, r) => s + r.delta, 0);
                  const totalAfter  = data.rows.reduce((s, r) => s + (r.matched ? r.new_total : 0), 0);
                  return (
                    <tr style={{ borderBottom: '1px solid var(--lg-border)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Розмір</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Було ({totalBefore})</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Додається ({totalDelta > 0 ? `+${totalDelta}` : totalDelta})</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Стане ({totalAfter})</th>
                      <th style={{ padding: '4px 8px' }}></th>
                    </tr>
                  );
                })()}
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.val} style={{ opacity: r.matched ? 1 : 0.5 }}>
                    <td style={{ padding: '4px 8px' }}>{r.val}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.matched ? r.current_qty : '—'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {r.delta > 0 ? `+${r.delta}` : r.delta}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: r.will_change ? 700 : 400,
                                  color: r.will_change ? 'var(--lg-accent)' : undefined }}>
                      {r.matched ? r.new_total : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', fontSize: 11, opacity: 0.6 }}>
                      {!r.matched ? (r.delta !== 0 ? 'немає в сітці EasyDrop' : '') : r.will_change ? 'зміниться' : 'без змін'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}

          {data && mode === 'restock' && (
            <div>
              <button type="button" onClick={toggleHistory}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, opacity: 0.7, padding: 0 }}>
                {historyOpen ? '▾' : '▸'} Історія змін
              </button>
              {historyOpen && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
                  {history === null && <div style={{ fontSize: 12, opacity: 0.6 }}>Завантаження…</div>}
                  {history?.length === 0 && <div style={{ fontSize: 12, opacity: 0.6 }}>Історії ще немає</div>}
                  {history?.map(h => (
                    <div key={h.id} style={{ fontSize: 12, padding: '6px 8px', border: '1px solid var(--lg-border)', borderRadius: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong>{EASYDROP_ACTION_LABELS[h.action] || h.action}</strong>
                        <span style={{ opacity: 0.6 }}>{h.created_at ? new Date(h.created_at).toLocaleString('uk-UA') : ''}</span>
                      </div>
                      {h.action === 'restock' && h.details?.sizes?.matched?.length > 0 && (
                        <div style={{ opacity: 0.7, marginTop: 2 }}>
                          {h.details.sizes.matched.map(m => `${m.val}: ${m.delta > 0 ? '+' : ''}${m.delta}`).join(', ')}
                        </div>
                      )}
                      {h.action === 'restock' && h.details?.price?.ok && (
                        <div style={{ opacity: 0.7, marginTop: 2 }}>Ціна: {h.details.price.old} → {h.details.price.new}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {data && (
            <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--lg-border)', paddingTop: 14 }}>
              <button className="btn btn-primary" disabled={confirming} onClick={handleConfirm}>
                {confirming ? 'Виконання…' : (mode === 'create' ? 'Підтвердити і створити' : 'Підтвердити і оновити')}
              </button>
              <button className="btn btn-secondary" disabled={confirming} onClick={onClose}>Скасувати</button>
              {mode === 'restock' && onUnlink && (
                <button className="btn btn-secondary" style={{ marginLeft: 'auto', color: '#f87171' }}
                  disabled={confirming} onClick={onUnlink}>
                  Відв'язати від EasyDrop
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Вкладка Получено (с партиями) ──────────────────────────────────────────
function ReceivedSubtab({ search = '' }) {
  const [batches, setBatches] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedBatch, setExpandedBatch] = useState('unassigned');
  const [batchModal, setBatchModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [tgModal, setTgModal] = useState(null);
  const [easydropModal, setEasydropModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [rate, setRate] = useState(46);
  const [kgPrice, setKgPrice] = useState(18);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [receivedRes, publishedRes, batchesRes, cfgRes] = await Promise.all([
        cheeseAPI.listTransit('received'),
        cheeseAPI.listTransit('published'),
        cheeseAPI.listBatches(),
        configAPI.getConfig().catch(() => ({ data: [] })),
      ]);
      setItems([...receivedRes.data, ...publishedRes.data]);
      setBatches(batchesRes.data);
      if (cfgRes.data) {
        const byKey = Object.fromEntries(cfgRes.data.map(c => [c.key, c.value]));
        if (byKey.exchange_rate) setRate(parseFloat(byKey.exchange_rate) || 46);
        if (byKey.kg_delivery_price) setKgPrice(parseFloat(byKey.kg_delivery_price) || 18);
      }
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handlePatch = async (id, fields) => {
    try {
      const res = await cheeseAPI.updateTransit(id, fields);
      setItems(prev => prev.map(it => it.id === id ? res.data : it));
    } catch { /* silent */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить товар?')) return;
    await cheeseAPI.deleteTransit(id);
    setItems(prev => prev.filter(it => it.id !== id));
    setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const handleSave = (saved) => setItems(prev => prev.map(it => it.id === saved.id ? saved : it));
  const handleTgDone = async () => {
    if (tgModal?.id) {
      await cheeseAPI.updateTransit(tgModal.id, { status: 'published' }).catch(() => {});
    }
    const [receivedR, publishedR] = await Promise.all([
      cheeseAPI.listTransit('received'),
      cheeseAPI.listTransit('published'),
    ]);
    setItems([...receivedR.data, ...publishedR.data]);
    setTgModal(null);
  };

  const reportResult = ({ easydrop_item_id, sizes, price, photo }) => {
    const parts = [`EasyDrop id: ${easydrop_item_id}`, `Оновлено розмірів: ${sizes.matched.length}`];
    if (sizes.unmatched_ours.length) parts.push(`Не знайдено в сітці EasyDrop: ${sizes.unmatched_ours.join(', ')}`);
    if (sizes.errors.length) parts.push(`Помилки розмірів: ${sizes.errors.map(e => `${e.val} (${e.error})`).join('; ')}`);
    if (price) {
      parts.push(price.ok ? `Ціна: ${price.old} → ${price.new}` : `Помилка ціни: ${price.error}`);
    }
    if (photo) {
      parts.push(photo.ok ? 'Фото завантажено' : `Фото не завантажено: ${photo.error || 'немає фото у картки'}`);
    }
    alert(parts.join('\n'));
  };

  const doCreateNew = (item) => {
    setEasydropModal(null);
    setPreviewModal({ mode: 'create', item });
  };

  const handleEasydrop = async (item) => {
    if (item.easydrop_item_id) {
      // Вже прив'язаний — попередній перегляд змін (розміри + ціна) перед записом.
      setPreviewModal({ mode: 'restock', item });
      return;
    }
    // Ще не прив'язаний — спочатку перевіряємо, чи такий товар вже є
    // в EasyDrop (за артикулом), щоб не наплодити дублікатів.
    let match = null;
    try {
      const res = await cheeseAPI.easydropMatch(item.id);
      match = res.data.match;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Помилка перевірки EasyDrop');
      return;
    }
    setEasydropModal({ item, autoMatch: match });
  };

  const handleNextStatus = async (id) => {
    const res = await cheeseAPI.updateTransit(id, { status: 'published' });
    setItems(prev => prev.map(it => it.id === id ? res.data : it));
    setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleSelectAll = (ids) => {
    const allSel = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (allSel) ids.forEach(id => n.delete(id));
      else ids.forEach(id => n.add(id));
      return n;
    });
  };

  const handleAssignToBatch = async (batchId) => {
    const ids = [...selectedIds];
    await cheeseAPI.assignItemsToBatch(batchId, ids);
    setItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, batch_id: batchId } : it));
    setSelectedIds(new Set());
  };

  const handleBatchSave = (saved) => {
    setBatches(prev => {
      const idx = prev.findIndex(b => b.id === saved.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n; }
      return [saved, ...prev];
    });
    setBatchModal(null);
  };

  const handleBatchSaveAndAssign = async (saved) => {
    handleBatchSave(saved);
    if (selectedIds.size > 0) {
      const ids = [...selectedIds];
      await cheeseAPI.assignItemsToBatch(saved.id, ids);
      setItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, batch_id: saved.id } : it));
      setSelectedIds(new Set());
    }
    setExpandedBatch(saved.id);
  };

  const handleBatchDelete = async (id) => {
    if (!window.confirm('Удалить партию? Товары останутся без партии.')) return;
    await cheeseAPI.deleteBatch(id);
    setBatches(prev => prev.filter(b => b.id !== id));
    setItems(prev => prev.map(it => it.batch_id === id ? { ...it, batch_id: null } : it));
  };

  const handleRemoveFromBatch = async (itemId) => {
    await cheeseAPI.updateTransit(itemId, { batch_id: null });
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, batch_id: null } : it));
  };

  const allSizes = ['35','36','37','38','39','40','41','42','43','44','45','46','47'];
  const q = search.trim().toLowerCase();
  const filterItems = list => q ? list.filter(it =>
    (it.name||'').toLowerCase().includes(q) ||
    (it.catalog_sku||'').toLowerCase().includes(q) ||
    (it.supplier_article||'').toLowerCase().includes(q)
  ) : list;

  const unassigned = filterItems(items.filter(it => !it.batch_id));

  return (
    <div className="cheese-subtab">
      {loadError && <div className="cheese-load-error">Ошибка загрузки. <button className="btn btn-sm btn-secondary" onClick={load}>Повторить</button></div>}
      <div className="cheese-toolbar">
        <button className="btn btn-primary btn-sm" onClick={() => setBatchModal({ batch: null })}>
          + Создать партию
        </button>
        {selectedIds.size > 0 && (
          <>
            <span className="cheese-toolbar-hint">Выбрано: {selectedIds.size}</span>
            <select className="cheese-assign-select"
              value=""
              onChange={e => { if (e.target.value) handleAssignToBatch(parseInt(e.target.value)); }}>
              <option value="" disabled>→ Добавить в партию…</option>
              {batches.map(b => <option key={b.id} value={b.id}>{b.name}{b.received_date ? ` (${b.received_date})` : ''}</option>)}
            </select>
            <button className="btn btn-secondary btn-sm"
              onClick={() => setBatchModal({ batch: null, withItems: true })}>
              + Новая партия из выбранных
            </button>
          </>
        )}
        <span className="cheese-toolbar-right" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--lg-text-muted)' }}>
          {items.length} товаров · {batches.length} партий
        </span>
      </div>

      {loading && <div className="cheese-empty">Загрузка…</div>}

      {!loading && (
        <div className="cheese-received-scroll">
        <div className="cheese-received-body">

          {/* Без партии */}
          <div className="cheese-batch-card cheese-batch-card--unassigned">
            <div className="cheese-batch-header" onClick={() => setExpandedBatch(expandedBatch === 'unassigned' ? null : 'unassigned')}>
              <span className="cheese-batch-arrow">{expandedBatch === 'unassigned' ? '▾' : '▸'}</span>
              <span className="cheese-batch-name" style={{ color: 'var(--lg-text-muted)' }}>Без партии</span>
              <span className="cheese-batch-count">{unassigned.length} шт</span>
            </div>
            {expandedBatch === 'unassigned' && (
              <div className="cheese-batch-items">
                <TransitTableSection
                  items={unassigned} allSizes={allSizes} rate={rate} kgPrice={kgPrice}
                  selectedIds={selectedIds} onToggle={toggleSelect} onToggleAll={toggleSelectAll}
                  onEdit={setEditModal} onTg={setTgModal}
                  onDelete={handleDelete} onPatch={handlePatch} onNextStatus={handleNextStatus}
                  onEasydrop={handleEasydrop}
                />
              </div>
            )}
          </div>

          {/* Партии */}
          {batches.map(batch => {
            const bItems = filterItems(items.filter(it => it.batch_id === batch.id));
            const allBatchItems = items.filter(it => it.batch_id === batch.id);
            const isExp = expandedBatch === batch.id;
            return (
              <div key={batch.id} className="cheese-batch-card">
                <div className="cheese-batch-header" onClick={() => setExpandedBatch(isExp ? null : batch.id)}>
                  <span className="cheese-batch-arrow">{isExp ? '▾' : '▸'}</span>
                  <div className="cheese-batch-meta">
                    <span className="cheese-batch-name">{batch.name}</span>
                    {batch.received_date && <span className="cheese-batch-date">{batch.received_date}</span>}
                    <span className="cheese-batch-count">{allBatchItems.length} шт</span>
                  </div>
                  {batch.photo_url && (
                    <img className="cheese-batch-thumb" src={proxyImageUrl(batch.photo_url)} alt=""
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div className="cheese-batch-actions" onClick={e => e.stopPropagation()}>
                    <button className="cheese-icon-btn" onClick={() => setBatchModal({ batch })} title="Редактировать">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="cheese-icon-btn cheese-icon-btn--del" onClick={() => handleBatchDelete(batch.id)} title="Удалить партию">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </div>
                </div>
                <div className="cheese-batch-items">
                  <TransitTableSection
                    items={bItems} allSizes={allSizes} rate={rate} kgPrice={kgPrice}
                    selectedIds={null} onToggle={null} onToggleAll={null}
                    onEdit={setEditModal} onTg={setTgModal}
                    onDelete={handleDelete} onPatch={handlePatch} onNextStatus={handleNextStatus}
                    onEasydrop={handleEasydrop}
                  />
                  {bItems.length > 0 && (
                    <div style={{ padding: '6px 12px' }}>
                      {bItems.map(it => (
                        <button key={it.id} className="cheese-batch-remove-btn"
                          onClick={() => handleRemoveFromBatch(it.id)}
                          title={`Убрать «${it.name}» из партии`}>
                          ✕ {it.name || it.catalog_sku}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {isExp && (() => {
                  const blocks = (() => { try { return batch.rich_notes ? JSON.parse(batch.rich_notes) : []; } catch { return []; } })();
                  if (blocks.length) return (
                    <div className="bnote-preview">
                      {blocks.map(bl => (
                        <div key={bl.id} className="bnote-preview-block">
                          {bl.photo_url && <img className="bnote-preview-img" src={fixUploadUrl(bl.photo_url)} alt="" onError={e => { e.target.style.display='none'; }} />}
                          {bl.text && <p className="bnote-preview-text">{bl.text}</p>}
                        </div>
                      ))}
                    </div>
                  );
                  if (batch.notes) return <div className="cheese-batch-notes">{batch.notes}</div>;
                  return null;
                })()}
              </div>
            );
          })}

          {items.length === 0 && <div className="cheese-empty">Нет полученных товаров</div>}
        </div>
        </div>
      )}

      {batchModal !== null && (
        <BatchModal
          batch={batchModal.batch}
          onSave={batchModal.withItems ? handleBatchSaveAndAssign : handleBatchSave}
          onClose={() => setBatchModal(null)}
        />
      )}
      {editModal && <TransitModal item={editModal === 'create' ? null : editModal} onClose={() => setEditModal(null)} onSave={handleSave} />}
      {tgModal && <TelegramModal item={tgModal} onClose={() => setTgModal(null)} onDone={handleTgDone} />}
      {easydropModal && (
        <EasydropLinkModal
          item={easydropModal.item}
          autoMatch={easydropModal.autoMatch}
          onClose={() => setEasydropModal(null)}
          onLinked={(easydrop_item_id) => {
            setItems(prev => prev.map(it => it.id === easydropModal.item.id ? { ...it, easydrop_item_id } : it));
            setEasydropModal(null);
          }}
          onCreateNew={() => doCreateNew(easydropModal.item)}
        />
      )}
      {previewModal && (
        <EasydropPreviewModal
          mode={previewModal.mode}
          item={previewModal.item}
          onClose={() => setPreviewModal(null)}
          onDone={(data) => {
            if (previewModal.mode === 'create') {
              setItems(prev => prev.map(it => it.id === previewModal.item.id ? { ...it, easydrop_item_id: data.easydrop_item_id } : it));
            }
            setPreviewModal(null);
            reportResult(data);
          }}
          onUnlink={previewModal.mode === 'restock' ? async () => {
            if (!window.confirm(`Відв'язати «${previewModal.item.name}» від EasyDrop (id ${previewModal.item.easydrop_item_id})? Сам товар в EasyDrop не зміниться.`)) return;
            try {
              await cheeseAPI.easydropUnlink(previewModal.item.id);
              setItems(prev => prev.map(it => it.id === previewModal.item.id ? { ...it, easydrop_item_id: null } : it));
              setPreviewModal(null);
            } catch (e) {
              alert(e?.response?.data?.detail || 'Помилка відв\'язки');
            }
          } : undefined}
        />
      )}
    </div>
  );
}


// ── Вкладка Категории ──────────────────────────────────────────────────────
function CategoriesSubtab({ search = '' }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try { const res = await cheeseAPI.listCategories(); setCategories(res.data); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить категорию?')) return;
    try {
      await cheeseAPI.deleteCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (e) { alert('Ошибка: ' + (e?.response?.data?.detail || e.message)); }
  };

  const handleSave = (saved) => {
    setCategories(prev => {
      const idx = prev.findIndex(c => c.id === saved.id);
      if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n; }
      return [...prev, saved].sort((a, b) => a.category_id - b.category_id);
    });
    setModal(null);
  };

  const catQ = search.trim().toLowerCase();
  const visibleCategories = catQ
    ? categories.filter(c =>
        (c.name || '').toLowerCase().includes(catQ) ||
        String(c.category_id).includes(catQ) ||
        (c.last_article || '').toLowerCase().includes(catQ)
      )
    : categories;

  return (
    <div className="cheese-subtab">
      {loadError && <div className="cheese-load-error">Ошибка загрузки. <button className="btn btn-sm btn-secondary" onClick={load}>Повторить</button></div>}
      <div className="cheese-toolbar">
        <button className="btn btn-primary btn-sm" onClick={() => setModal('create')}>
          + Добавить категорию
        </button>
        <div className="cheese-toolbar-stats">
          <span>{visibleCategories.length}{catQ ? `/${categories.length}` : ''} категорий</span>
        </div>
      </div>

      {loading && <div className="cheese-empty">Загрузка…</div>}
      {!loading && categories.length === 0 && (
        <div className="cheese-empty"><div>Нет категорий</div></div>
      )}
      {!loading && categories.length > 0 && visibleCategories.length === 0 && (
        <div className="cheese-empty">Ничего не найдено по запросу «{search}»</div>
      )}
      {!loading && visibleCategories.length > 0 && (
        <div className="cheese-transit-table-wrap">
          <table className="cheese-transit-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>ID</th>
                <th>Название</th>
                <th>Последний артикул</th>
                <th style={{ width: 90 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleCategories.map(cat => (
                <tr key={cat.id}>
                  <td className="cheese-tr-num">{cat.category_id}</td>
                  <td>{cat.name}</td>
                  <td>{cat.last_article}</td>
                  <td className="cheese-tr-actions-cell">
                    <button className="cheese-icon-btn" onClick={() => setModal(cat)} title="Редактировать">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="cheese-icon-btn cheese-icon-btn--del" onClick={() => handleDelete(cat.id)} title="Удалить">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <CategoryModal
          item={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ── Главный компонент ──────────────────────────────────────────────────────
export default function CheeseTab() {
  const [subtab, setSubtab] = useState('orders');
  const [search, setSearch] = useState('');

  const switchTab = (tab) => { setSubtab(tab); setSearch(''); };

  return (
    <div className="cheese-tab">
      <div className="cheese-subtabs">
        <button
          className={`cheese-subtab-btn${subtab === 'orders' ? ' cheese-subtab-btn--active' : ''}`}
          onClick={() => switchTab('orders')}>
          Закупка
        </button>
        <button
          className={`cheese-subtab-btn${subtab === 'transit' ? ' cheese-subtab-btn--active' : ''}`}
          onClick={() => switchTab('transit')}>
          В пути
        </button>
        <button
          className={`cheese-subtab-btn${subtab === 'received' ? ' cheese-subtab-btn--active' : ''}`}
          onClick={() => switchTab('received')}>
          Получено
        </button>
        <button
          className={`cheese-subtab-btn${subtab === 'published' ? ' cheese-subtab-btn--active' : ''}`}
          onClick={() => switchTab('published')}>
          Опубликовано
        </button>
        <button
          className={`cheese-subtab-btn${subtab === 'categories' ? ' cheese-subtab-btn--active' : ''}`}
          onClick={() => switchTab('categories')}>
          Категории
        </button>
        <div className="cheese-header-search">
          <input
            type="text"
            className="cheese-search-input"
            placeholder="Поиск по названию, артикулу…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="cheese-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>
      </div>

      {subtab === 'orders'     && <OrdersSubtab search={search} />}
      {subtab === 'transit'    && <TransitSubtab search={search} statusFixed="transit" />}
      {subtab === 'received'   && <ReceivedSubtab search={search} />}
      {subtab === 'published'  && <TransitSubtab search={search} statusFixed="published" />}
      {subtab === 'categories' && <CategoriesSubtab search={search} />}
    </div>
  );
}
